/* eslint-disable no-useless-computed-key */
import _ from "lodash";
import { startOfDay, startOfYear, subDays } from "date-fns";
import {
  autoAddFormsToTransaction,
  autoAddTaskTemplatesToTransaction,
  convertAddressFull,
  convertAddressStreetAndUnit,
  createInitialSigningDetails,
  createSearchArray,
  createSuggestedAnnots,
  createTransactionFields,
  docSigningComplete,
  getAgentClientParties,
  partyIsAgent,
  partyIsMyCoAgent,
  partyIsMyTc,
  partyIsOppositeSideClient,
  personExists,
  updateFormListForBrokerage,
} from "../common/util/util";
import { parseDateSafe } from "../common/util/dateUtils";
import { app } from "../config/firebase";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  getFirestore,
  collection,
  Timestamp,
  doc,
  addDoc,
  getDocs,
  updateDoc,
  query,
  orderBy,
  where,
  deleteDoc,
  serverTimestamp,
  increment,
  limit,
  startAfter,
  writeBatch,
  limitToLast,
  endBefore,
  arrayUnion,
  arrayRemove,
  getDoc,
  setDoc,
  or,
  and,
  onSnapshot,
} from "firebase/firestore";
import {
  functionAddCoAgentIdToTransaction,
  functionAddTcIdToTransaction,
  functionAddAgentAsTcToTransaction,
  functionLinkUserToParty,
  functionRemoveCoAgentIdFromTransaction,
  functionRemoveTcIdFromTransaction,
  functionRemoveAgentAsTcFromTransaction,
  functionSendNewTransactionNotificationEmail,
  functionSendDocumentSignedNotificationEmails,
  functionSendTransactionStatusChangedNotificationEmails,
} from "./functionsService";
import { subMinutes } from "date-fns";

const db = getFirestore(app);
const auth = getAuth(app);

let authClaims = {};

// Query builders throughout this file read `authClaims` synchronously, but
// it's only populated once getIdTokenResult() resolves - an extra async
// round-trip beyond onAuthStateChanged firing. `authClaimsReady` lets
// callers (see authActions.js) await that round-trip before treating the
// signed-in user as ready, so Firestore listeners started right after login
// don't fire with stale/empty claims and get denied by security rules.
let resolveAuthClaimsReady;
export let authClaimsReady = new Promise((resolve) => {
  resolveAuthClaimsReady = resolve;
});

onAuthStateChanged(auth, (user) => {
  if (user) {
    authClaimsReady = new Promise((resolve) => {
      resolveAuthClaimsReady = resolve;
    });
    auth.currentUser.getIdTokenResult().then((userClaim) => {
      authClaims = userClaim?.claims;
      resolveAuthClaimsReady();
    });
  } else {
    authClaims = {};
    resolveAuthClaimsReady();
  }
});

export function dataFromSnapshot(snapshot) {
  if (!snapshot.exists) return undefined;
  const data = snapshot.data();

  for (const prop in data) {
    if (data.hasOwnProperty(prop)) {
      if (data[prop] instanceof Timestamp) {
        data[prop] = data[prop].toDate();
      }
    }
  }

  return {
    ...data,
    id: snapshot.id,
  };
}

///////////////////////////////////////////////////////////////
/////////////////////  TRANSACTIONS  //////////////////////////
///////////////////////////////////////////////////////////////

export function fetchTransActiveFromDb(id = null) {
  let queryRef = query(
    collection(db, "transactions"),
    where("active", "==", true)
  );
  // console.log("authClaims", authClaims);
  // console.log("authcurrentUser", auth.currentUser);
  if (authClaims?.r === "a" && authClaims?.a) {
    queryRef = query(queryRef, where("userId", "in", authClaims.a));
  } else if (authClaims?.r === "t") {
    queryRef = query(queryRef, where("tcId", "==", auth.currentUser.uid));
  } else if (authClaims?.r === "g" && authClaims?.m) {
    if (id) {
      // Manager assistant viewing an agent's transactions - include where agent is userId OR coAgentId
      queryRef = query(
        queryRef,
        and(
          where("managerId", "==", authClaims.m[0]),
          or(
            where("userId", "==", id),
            where("coAgentId", "==", id)
          )
        )
      );
    }
    else {
      queryRef = query(queryRef,
        where("managerId", "==", authClaims.m[0]));
    }
  } else if (id) {    // manager logged in as agent
    // Manager viewing an agent's transactions - include where agent is userId OR coAgentId
    queryRef = query(
      queryRef,
      and(
        where("managerId", "==", auth.currentUser.uid),
        or(
          where("userId", "==", id),
          where("coAgentId", "==", id)
        )
      )
    );
  } else {
    queryRef = query(
      queryRef,
      or(
        where("userId", "==", auth.currentUser.uid),
        where("coAgentId", "==", auth.currentUser.uid),
        where("tcId", "==", auth.currentUser.uid),
        where("agentAsTcId", "==", auth.currentUser.uid)
      )
    );
  }
  return query(queryRef, orderBy("updatedAt", "desc"));
}

export function fetchTransactionsForUserFromDb() {
  return query(
    collection(db, "transactions"),
    where("sharingWith", "array-contains", auth.currentUser.email)
  );
}

export function fetchTransactionFromDb(transactionId) {
  // SHOULDN'T THIS HAVE MORE SECURITY?
  const docRef = doc(db, "transactions", transactionId);
  return docRef;
}

export function fetchTransactionsStatusFromDb(
  limitNumber,
  direction,
  referenceSnapshot = null,
  searchTerms = null,
  id = null,
  status = "Complete"
) {
  let q = query(collection(db, "transactions"));
  if (authClaims?.r === "a" && authClaims?.a) {
    q = query(q, where("userId", "in", authClaims.a));
  } else if (authClaims?.r === "t") {
    q = query(q, where("tcId", "==", auth.currentUser.uid));
  } else if (authClaims?.r === "g" && authClaims?.m) {
    if (id) {
      // Manager assistant viewing an agent's transactions - include where agent is userId OR coAgentId
      q = query(q,
        and( 
          where("managerId", "==", authClaims.m[0]),
          or(
            where("userId", "==", id),
            where("coAgentId", "==", id)
          )
      ));
    }
    else {
      q = query(q,
        where("managerId", "==", authClaims.m[0]));
    }
  } else if (id) {  // manager logged in as agent
    // Manager viewing an agent's transactions - include where agent is userId OR coAgentId
    q = query(
      q,
      and(
        where("managerId", "==", auth.currentUser.uid),
        or(
          where("userId", "==", id),
          where("coAgentId", "==", id)
        )
      )
    );
  } else {
    q = query(
      q,
      or(
        where("userId", "==", auth.currentUser.uid),
        where("coAgentId", "==", auth.currentUser.uid),
        where("tcId", "==", auth.currentUser.uid),
        where("agentAsTcId", "==", auth.currentUser.uid)
      )
    );
  }
  if (searchTerms) {
    q = query(q, where("searchArray", "array-contains-any", searchTerms));
  }
  q = query(q, where("status", "==", status));
  q = query(q, orderBy("updatedAt", "desc"));

  if (referenceSnapshot && direction === "next") {
    q = query(q, startAfter(referenceSnapshot), limit(limitNumber));
  }
  // if (referenceSnapshot && direction === 'initial') {
  //   // q = query(q, startAt(referenceSnapshot), limit(limitNumber))
  //   q = query(q, limit(limitNumber))
  // }
  if ((!referenceSnapshot && direction === "next") || direction === "initial") {
    q = query(q, limit(limitNumber));
  }
  if (referenceSnapshot && direction === "previous") {
    q = query(q, endBefore(referenceSnapshot), limitToLast(limitNumber));
  }
  if (!referenceSnapshot && direction === "previous") {
    q = query(q, limitToLast(limitNumber));
  }
  return q;
}

export function fetchTransactionsAllFromDb(
  limitNumber,
  direction,
  referenceSnapshot = null,
  searchTerms = null,
  id = null
) {
  let q = query(collection(db, "transactions"));
  if (authClaims?.r === "a" && authClaims?.a) {
    q = query(q, where("userId", "in", authClaims.a));
  } else if (authClaims?.r === "t") {
    q = query(q, where("tcId", "==", auth.currentUser.uid));
  } else if (authClaims?.r === "g" && authClaims?.m) {
    if (id) {
      q = query(q,
        where("userId", "==", id),
        where("managerId", "==", authClaims.m[0]));
    }
    else {
      q = query(q,
        where("managerId", "==", authClaims.m[0]));
    }
  } else if (id) {
    q = query(
      q,
      where("userId", "==", id),
      where("managerId", "==", auth.currentUser.uid)
    );
  } else {
    q = query(
      q,
      or(
        where("userId", "==", auth.currentUser.uid),
        where("coAgentId", "==", auth.currentUser.uid),
        where("tcId", "==", auth.currentUser.uid),
        where("agentAsTcId", "==", auth.currentUser.uid)
      )
    );
  }
  if (searchTerms) {
    q = query(q, where("searchArray", "array-contains-any", searchTerms));
  }
  q = query(q, orderBy("updatedAt", "desc"));

  if (referenceSnapshot && direction === "next") {
    q = query(q, startAfter(referenceSnapshot), limit(limitNumber));
  }
  // if (referenceSnapshot && direction === 'initial') {
  //   // q = query(q, startAt(referenceSnapshot), limit(limitNumber))
  //   q = query(q, limit(limitNumber))
  // }
  if ((!referenceSnapshot && direction === "next") || direction === "initial") {
    q = query(q, limit(limitNumber));
  }
  if (referenceSnapshot && direction === "previous") {
    q = query(q, endBefore(referenceSnapshot), limitToLast(limitNumber));
  }
  if (!referenceSnapshot && direction === "previous") {
    q = query(q, limitToLast(limitNumber));
  }
  return q;
}

export async function addTransactionToDb(
  transaction,
  people,
  forms,
  currentUserProfile,
  mlsData,
  agentsForAssistant,
  formTemplates,
  taskTemplates,
  agentUserProfile = null
) {
  const batch = writeBatch(db);
  const transactionId = doc(collection(db, "transactions")).id;

  let agentAndAssistantProfiles = {
    createdBy: currentUserProfile.role || "agent",
  };
  if (currentUserProfile.role === "assistant" && transaction.agentId) {
    const agent = agentsForAssistant.filter(
      (agent) => agent.userId === transaction.agentId
    )?.[0];
    Object.assign(agentAndAssistantProfiles, {
      hasAssistant: true,
      assistant: currentUserProfile,
      assistantEmail: currentUserProfile.email,
      agentProfile: agent || {},
      managerId: agent?.managerId || "",
      managerDetails: agent?.managerDetails || {},
      userId: agent?.userId || "",
    });
  }
  else if (currentUserProfile.role === "agent" && currentUserProfile.assistantEmail) {
    Object.assign(agentAndAssistantProfiles, {
      hasAssistant: true,
      assistant: currentUserProfile.assistant || {},
      agentProfile: currentUserProfile,
      managerId: currentUserProfile.managerId || "",
      managerDetails: currentUserProfile.managerDetails || {},
      userId: currentUserProfile.userId || "",
      assistantEmail: currentUserProfile.assistantEmail,
    });
  }
  else if ((currentUserProfile.role === "manager" || currentUserProfile.role === "managerassistant") && agentUserProfile) {
    // Manager creating transaction for an agent
    Object.assign(agentAndAssistantProfiles, {
      hasAssistant: agentUserProfile.assistantEmail ? true : false,
      assistant: agentUserProfile.assistant || {},
      agentProfile: agentUserProfile || {},
      managerId: agentUserProfile.managerId || "",
      managerDetails: {
        firstName: agentUserProfile.managerDetails?.firstName || "",
        lastName: agentUserProfile.managerDetails?.lastName || "",
        managerEmail: agentUserProfile.managerDetails?.email || "",
        sendEmailNotificationsOfNewTransactions: agentUserProfile.managerDetails?.sendEmailNotificationsOfNewTransactions || false,
      },
      userId: agentUserProfile.userId || "",
      assistantEmail: agentUserProfile.assistantEmail || "",
    });
  }
  else {
    Object.assign(agentAndAssistantProfiles, {
      hasAssistant: false,
      assistant: {},
      agentProfile: currentUserProfile,
      managerId: currentUserProfile.managerId || "",
      managerDetails: currentUserProfile.managerDetails || {},
      userId: currentUserProfile.userId || "",
      assistantEmail: "",
    });
  }
  Object.assign(transaction, agentAndAssistantProfiles);

  transaction["id"] = transactionId;
  transaction["clientSecondaryExists"] = transaction.clientSecondary.exists;
  transaction["clientThirdExists"] = transaction.clientThird.exists;

  // Add MLS data to transaction
  // Note: Address and pic already added in form
  let listingAgentPartyId = "";
  if (mlsData) {
    if (mlsData.propertyDetails) {
      transaction.propertyDetails = mlsData.propertyDetails;
    }
    if (
      mlsData.listingAgent &&
      mlsData.listingAgent?.firstName &&
      mlsData.listingAgent?.lastName &&
      transaction.agentRepresents === "Buyer"
    ) {
      if (mlsData.listingAgent.email) {
        mlsData.listingAgent.email = mlsData.listingAgent.email.toLowerCase();
      } else {
        mlsData.listingAgent.email = "";
      }
      listingAgentPartyId = doc(collection(db, "parties")).id;
      batch.set(doc(db, "parties", listingAgentPartyId), {
        ...mlsData.listingAgent,
        userId: transaction["userId"],
        id: listingAgentPartyId,
        managerId: transaction.managerId,
        type: "Party",
        role: "Listing Agent",
        transactionId: transactionId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    }
  }
  let owner1PartyId = "";
  let owner2PartyId = "";

  if (transaction.agentRepresents === "Buyer") {
    if (transaction.owner?.entityName || transaction.owner?.lastName) {
      owner1PartyId = doc(collection(db, "parties")).id;
      batch.set(doc(db, "parties", owner1PartyId), {
        ...transaction.owner,
        userId: transaction["userId"],
        id: owner1PartyId,
        managerId: transaction.managerId,
        type: "Party",
        role: "Seller",
        transactionId: transactionId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      if (transaction.owner2?.entityName || transaction.owner2?.lastName) {
        owner2PartyId = doc(collection(db, "parties")).id;
        batch.set(doc(db, "parties", owner2PartyId), {
          ...transaction.owner2,
          userId: transaction["userId"],
          id: owner2PartyId,
          managerId: transaction.managerId,
          type: "Party",
          role: "Seller 2",
          transactionId: transactionId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
    }
  }

  if (transaction.agentRepresents === "Buyer") {
    transaction.status = "Active Buyer";
    transaction.client.role = "Buyer";
    if (transaction.clientSecondaryExists) {
      transaction.clientSecondary.role = "Buyer 2";
    }
    if (transaction.clientThirdExists) {
      transaction.clientThird.role = "Buyer 3";
    }
  } else {
    transaction.status = "Active Listing";
    transaction.client.role = "Seller";
    if (transaction.clientSecondaryExists) {
      transaction.clientSecondary.role = "Seller 2";
    }
    if (transaction.clientThirdExists) {
      transaction.clientThird.role = "Seller 3";
    }
  }

  if (currentUserProfile.role === "agent") {
    if (!personExists(people, transaction.client?.email)) {
      transaction.client.type = "Client";
      const personId = doc(collection(db, "people")).id;
      batch.set(doc(db, "people", personId), {
        ...transaction.client,
        userId: transaction["userId"],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    }
    if (
      transaction.clientSecondaryExists &&
      transaction.clientSecondary?.email
    ) {
      if (!personExists(people, transaction.clientSecondary?.email)) {
        transaction.clientSecondary.type = "Client";
        const personId = doc(collection(db, "people")).id;
        batch.set(doc(db, "people", personId), {
          ...transaction.clientSecondary,
          userId: transaction["userId"],
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
    }
    if (
      transaction.clientThirdExists &&
      transaction.clientThird?.email
    ) {
      if (!personExists(people, transaction.clientThird?.email)) {
        transaction.clientThird.type = "Client";
        const personId = doc(collection(db, "people")).id;
        batch.set(doc(db, "people", personId), {
          ...transaction.clientThird,
          userId: transaction["userId"],
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
    }
  }

  transaction["searchArray"] = createSearchArray(transaction);

  delete transaction.owner;
  delete transaction.owner2;
  delete transaction.addressGoogle;
  delete transaction.clientSelected;
  delete transaction.clientSecondarySelected;
  delete transaction.clientThirdSelected;

  const partyId = doc(collection(db, "parties")).id;
  transaction.client["id"] = partyId;

  // Build sharedEmailClients array for primary client
  let sharedEmailClients = [];
  let party2Id = null;
  let party3Id = null;

  console.log("transaction.clientSecondaryExists", transaction.clientSecondaryExists);
  if (transaction.clientSecondaryExists) {
    party2Id = doc(collection(db, "parties")).id;
    transaction.clientSecondary["id"] = party2Id;

    // console.log("transaction.clientSecondary.sharesEmailWithPrimary", transaction.clientSecondary.sharesEmailWithPrimary);
    // If secondary client shares email with primary, add to sharedEmailClients and copy email
    if (transaction.clientSecondary.sharesEmailWithPrimary) {
      sharedEmailClients.push({
        role: transaction.clientSecondary.role,
        odPartyId: party2Id,
      });
      // Copy primary client's email to secondary client
      transaction.clientSecondary.email = transaction.client.email;
    }
  }
// console.log("sharedEmailClients", sharedEmailClients);
  if (transaction.clientThirdExists) {
    party3Id = doc(collection(db, "parties")).id;
    transaction.clientThird["id"] = party3Id;

    // If third client shares email with primary, add to sharedEmailClients and copy email
    if (transaction.clientThird.sharesEmailWithPrimary) {
      sharedEmailClients.push({
        role: transaction.clientThird.role,
        odPartyId: party3Id,
      });
      // Copy primary client's email to third client
      transaction.clientThird.email = transaction.client.email;
    }
  }
  transaction.client.sharedEmailClients = sharedEmailClients;

  // Create primary client party with sharedEmailClients array
  batch.set(doc(db, "parties", partyId), {
    ...transaction.client,
    userId: transaction["userId"],
    id: partyId,
    managerId: transaction.managerId,
    type: "Client",
    transactionId: transactionId,
    sharedEmailClients: sharedEmailClients,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  if (transaction.clientSecondaryExists) {
    batch.set(doc(db, "parties", party2Id), {
      ...transaction.clientSecondary,
      userId: transaction["userId"],
      managerId: transaction.managerId,
      id: party2Id,
      type: "Client",
      transactionId: transactionId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }

  if (transaction.clientThirdExists) {
    batch.set(doc(db, "parties", party3Id), {
      ...transaction.clientThird,
      userId: transaction["userId"],
      managerId: transaction.managerId,
      id: party3Id,
      type: "Client",
      transactionId: transactionId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }
  batch.set(doc(db, "transactions", transactionId), {
    ...transaction,
    userId: transaction["userId"],
    createdAt: transaction.createdAt || serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  await batch.commit();

  // Add to publicListingData collection if it's a Seller transaction
  if (transaction.agentRepresents === "Seller") {
    try {
      await setDoc(doc(db, "publicListingData", transactionId), {
        address: {
          city: transaction.address?.city || "",
          state: transaction.address?.state || "",
          street: transaction.address?.street || "",
          unit: transaction.address?.unit || "",
          zipcode: transaction.address?.zipcode || "",
        },
        listingAgent: {
          brokerLicenseNumber: transaction.agentProfile?.brokerLicenseNumber || "",
          brokerageLicenseNumber: transaction.agentProfile?.brokerageLicenseNumber || "",
          brokerageName: transaction.agentProfile?.brokerageName || "",
          email: transaction.agentProfile?.email || "",
          firstName: transaction.agentProfile?.firstName || "",
          lastName: transaction.agentProfile?.lastName || "",
          middleName: transaction.agentProfile?.middleName || "",
          phone: transaction.agentProfile?.phone || "",
        },
        mlsInfo:
          [
            {
              mlsIdCode: currentUserProfile.mlsAccess?.[0]?.mlsIdCode || "",
              mlsName: currentUserProfile.mlsAccess?.[0]?.mlsName || "",
              mlsNumber: ""
            },
            {
              mlsIdCode: currentUserProfile.mlsAccess?.[1]?.mlsIdCode|| "",
              mlsName: currentUserProfile.mlsAccess?.[1]?.mlsName || "",
              mlsNumber: ""
            }
          ],
        propertyDetails: {
          county: transaction.propertyDetails?.county || "",
          legalDescription: transaction.propertyDetails?.legalDescription || "",
          earnestMoneyHolder: transaction.propertyDetails?.earnestMoneyHolder || "",
          exclusions: transaction.propertyDetails?.exclusions || "",
          inclusions: transaction.propertyDetails?.inclusions || "",
          yearBuilt: transaction.propertyDetails?.yearBuilt || "",
        },
        owner: {
          firstName: transaction.client?.firstName || "",
          middleName: transaction.client?.middleName || "",
          lastName: transaction.client?.lastName || "",
          isTrust: transaction.client?.isTrust || false,
          entityName: transaction.client?.entityName || "",
        },
        owner2: {
          firstName: transaction.clientSecondary?.firstName || "",
          middleName: transaction.clientSecondary?.middleName || "",
          lastName: transaction.clientSecondary?.lastName || "",
          isTrust: transaction.clientSecondary?.isTrust || false,
          entityName: transaction.clientSecondary?.entityName || "",
        },
        owner2Exists: transaction.clientSecondaryExists || false,
        owner3: {
          firstName: transaction.clientThird?.firstName || "",
          middleName: transaction.clientThird?.middleName || "",
          lastName: transaction.clientThird?.lastName || "",
          isTrust: transaction.clientThird?.isTrust || false,
          entityName: transaction.clientThird?.entityName || "",
        },
        owner3Exists: transaction.clientThirdExists || false,
        transactionStatus: "Active Listing",
        transactionId: transactionId,
        userId: transaction.userId || "",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      // Silently fail if adding to publicListingData fails
      console.log("Error adding to publicListingData:", error);
    }
  }

  // Send notification emails to manager, manager assistants, and assistants
  try {
    // if (currentUserProfile.role === "agent") {
      await sendNewTransactionNotificationEmail(transaction, currentUserProfile);
    // }
  } catch (e) {
    console.log("Error sending emails about new transaction: ", e);
  }

  if (mlsData?.listingAgent?.email && transaction.agentRepresents === "Buyer") {
    functionLinkUserToParty({
      partyId: listingAgentPartyId,
      party: mlsData.listingAgent,
    });
  }
  const formsForBrokerage = updateFormListForBrokerage(
    forms,
    currentUserProfile
  );
  if (formTemplates === null) {
    formTemplates = fetchFormTemplatesFromDb();
    if (currentUserProfile.managerId) {
      const userManagerFormTemplates = fetchManagerFormTemplatesFromDb(currentUserProfile.managerId);
      formTemplates = {
        ...formTemplates,
        ...userManagerFormTemplates,
      }
    } 
  }
  autoAddFormsToTransaction(transaction, formsForBrokerage, currentUserProfile, formTemplates);

  // Auto-add task templates to new transaction
  if (taskTemplates && taskTemplates.length > 0) {
    autoAddTaskTemplatesToTransaction(transaction, taskTemplates, currentUserProfile);
  }

  return transactionId;
}

export function updateTransactionInDb(
  transactionId,
  transactionFields,
  updateSearchArray = false
) {
  if (updateSearchArray) {
    transactionFields["searchArray"] = createSearchArray(transactionFields);
  }
  if (
    transactionFields.status === "Complete" ||
    transactionFields.status === "Archived"
  ) {
    transactionFields["active"] = false;
  } else {
    transactionFields["active"] = true;
  }
  const docRef = doc(db, "transactions", transactionId);

  return updateDoc(docRef, {
    ...transactionFields,
    updatedAt: serverTimestamp(),
  });
}

// This is needed for pagination so can update without changing order with updatedAt
export function updateTransStatusInDb(transactionId, transactionFields) {
  const docRef = doc(db, "transactions", transactionId);
  return updateDoc(docRef, {
    ...transactionFields,
  });
}

// Save a generated strategy (e.g. "pricing") on the transaction without
// disturbing any other strategy types already stored there.
export function updateTransactionStrategyInDb(transactionId, strategyType, strategyData) {
  const docRef = doc(db, "transactions", transactionId);
  return updateDoc(docRef, {
    [`strategy.${strategyType}`]: strategyData,
    updatedAt: serverTimestamp(),
  });
}

// Update arbitrary transaction settings fields without the active/status side effects
export function updateTransactionSettingsInDb(transactionId, fields) {
  const docRef = doc(db, "transactions", transactionId);
  return updateDoc(docRef, {
    ...fields,
    updatedAt: serverTimestamp(),
  });
}

// Add a single pending notification to the transaction's pendingNotifications array
export function addPendingNotificationToDb(transactionId, notification) {
  const docRef = doc(db, "transactions", transactionId);
  return updateDoc(docRef, {
    pendingNotifications: arrayUnion(notification),
  });
}

// Replace the transaction's pendingNotifications array (used to clear sent groups)
export function setPendingNotificationsInDb(transactionId, pendingNotifications) {
  const docRef = doc(db, "transactions", transactionId);
  return updateDoc(docRef, {
    pendingNotifications: pendingNotifications,
  });
}

export async function addTransClientInDb(transaction, clientForm) {
  const batch = writeBatch(db);
  const partyId = doc(collection(db, "parties")).id;
  clientForm["id"] = partyId;
  const transactionFields = createTransactionFields(transaction, clientForm);
 //console.log("addTransClientInDb transactionFields:", transactionFields);
  //console.log("addTransClientInDb clientForm.sharesEmailWithPrimary:", clientForm.sharesEmailWithPrimary);
  // console.log("addTransClientInDb transaction.client.email:", transaction.client.email);
  //console.log("transaction.clientSecondary.email", transaction.clientSecondary?.email);
  let clientEmail = clientForm.email;
  if (clientForm.sharesEmailWithPrimary) {
    clientEmail = transaction?.client?.email || "";
  }
  //console.log("addTransClientInDb clientEmail:", clientEmail);
  batch.set(doc(db, "parties", partyId), {
    ...clientForm,
    id: partyId,
    email: clientEmail,
    transactionId: transaction.id,
    userId: transaction.userId,
    tcId: transaction.tcId || null,
    agentAsTcId: transaction.agentAsTcId || "",
    managerId: transaction.managerId || "",
    coAgentId: transaction.coAgentId || "",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  const docRef = doc(db, "transactions", transaction.id);
  batch.update(docRef, {
    ...transactionFields,
    updatedAt: serverTimestamp(),
  });
  await batch.commit();
  return partyId;
}

export function updateTransClientInDb(transaction, clientForm, originalClientEmail, docsNeedSharingUpdated, tasksTransWithDateAll, keepOriginalEmailInSharing = false) {
  // john branch = changeEmail node server implements, my branch client side
  // keepOriginalEmailInSharing: If true, don't remove the originalClientEmail from sharingWith arrays.
  // This is used when a secondary client was sharing email with primary and now unchecks the checkbox.
  // In this case, we need to keep the primary's email in sharingWith but add the secondary's new email.
  const batch = writeBatch(db);
  const transactionFields = createTransactionFields(transaction, clientForm);
  const partyRef = doc(db, "parties", clientForm.id);
  batch.update(partyRef, {
    ...clientForm,
    updatedAt: serverTimestamp(),
  });
  
  const newClientEmail = clientForm.sharesEmailWithPrimary ? transaction.client.email : clientForm.email;

  if (clientForm.email !== originalClientEmail) {
    const transSharingWithArray = transaction.sharingWith || [];
    if (transSharingWithArray.includes(originalClientEmail)) {
      if (keepOriginalEmailInSharing) {
        // Don't remove the original email, just add the new one if not already present
        transactionFields["sharingWith"] = [...transSharingWithArray];
        if (!transSharingWithArray.includes(newClientEmail)) {
          transactionFields["sharingWith"].push(newClientEmail);
        }
      } else {
        transactionFields["sharingWith"] = transSharingWithArray.filter(
          (item) => item !== originalClientEmail
        );
        transactionFields["sharingWith"].push(newClientEmail);
      }

      transactionFields["sharingWithRole"] =
        _.cloneDeep(transaction.sharingWithRole) || {};
      let partyRoleCamel = _.camelCase(clientForm.role);
      transactionFields.sharingWithRole[partyRoleCamel] = newClientEmail; 
      //console.log("updateTransClientInDb transactionFields.sharingWithRole:", transactionFields.sharingWithRole);
    }
  }

  const transactionRef = doc(db, "transactions", transaction.id);
  batch.update(transactionRef, {
    ...transactionFields,
    updatedAt: serverTimestamp(),
  });
      // update docs
      // if email changed
  if (clientForm.email !== originalClientEmail && docsNeedSharingUpdated) {
    docsNeedSharingUpdated.forEach((document) => {
      let newFields = {};

      const signingRequestedForArray = document.signingRequestedFor || [];
      if (signingRequestedForArray.includes(originalClientEmail)) {
        if (keepOriginalEmailInSharing) {
          // Don't remove the original email from signingRequestedFor, just add the new one if not present
          newFields["signingRequestedFor"] = [...signingRequestedForArray];
          if (!signingRequestedForArray.includes(newClientEmail)) {
            newFields["signingRequestedFor"].push(newClientEmail);
          }
        } else {
          newFields["signingRequestedFor"] = signingRequestedForArray.filter(
            (item) => item !== originalClientEmail
          );
          newFields["signingRequestedFor"].push(newClientEmail);
        }
      }

      const sharingWithArray = document.sharingWith || [];
      if (sharingWithArray.includes(originalClientEmail)) {
        if (keepOriginalEmailInSharing) {
          // Don't remove the original email, just add the new one if not already present
          // Check if any other role (except clientForm.role) uses clientForm.email
          const clientRoleCamel = _.camelCase(clientForm.role);
          const sharingWithRole = document.sharingWithRole || {};
          let otherRoleUsesEmail = false;

          for (const [role, email] of Object.entries(sharingWithRole)) {
            if (role !== clientRoleCamel && email === originalClientEmail) {
              otherRoleUsesEmail = true;
              break;
            }
          }

          if (otherRoleUsesEmail) {
            // Another role uses this email, keep the original email in sharingWith
            newFields["sharingWith"] = [...sharingWithArray];
          } else {
            // No other role uses this email, remove the original email
            newFields["sharingWith"] = sharingWithArray.filter(
              (item) => item !== originalClientEmail
            );
          }
          if (!sharingWithArray.includes(newClientEmail)) {
            newFields["sharingWith"].push(newClientEmail);
          }
        } else {
          newFields["sharingWith"] = sharingWithArray.filter(
            (item) => item !== originalClientEmail
          );
          newFields["sharingWith"].push(newClientEmail);
        }
      }
      //console.log("updateTransClientInDb newFields.sharingWith:", newFields.sharingWith);

      newFields["sharingWithRole"] =
        _.cloneDeep(document.sharingWithRole) || {};
      let partyRoleCamel = _.camelCase(clientForm.role);
      newFields.sharingWithRole[partyRoleCamel] = newClientEmail;

      if (document.annotsToSign) {
        Object.entries(document.annotsToSign).forEach(([key, value]) => {
          if (
            value.signerEmail === originalClientEmail
          ) {
            newFields[`annotsToSign.${value.uniqueId}.signerEmail`] = newClientEmail;
          }
        });
      }
      // if (document.signerListInProgress?.length > 0) {
      //   const signerListWithOriginalEmail = document.signerListInProgress.filter((item) => item.email === originalClientEmail);

      // }
      if (document.signingDetails) {
        Object.entries(document.signingDetails).forEach(([key, value]) => {
          if (
            value.email === originalClientEmail
          ) {
            newFields[`signingDetails.${key}.email`] = newClientEmail;
          }
        });
      }
      const docRef = doc(db, "documents", document.id);
      batch.update(docRef, { ...newFields });
    });
    // update tasks
    if (tasksTransWithDateAll?.length > 0) {
      tasksTransWithDateAll.forEach( (task) => {
        if (task?.sharingWith?.includes(originalClientEmail)) {
          let newSharingWith;
          if (keepOriginalEmailInSharing) {
            // Don't remove the original email, just add the new one if not present
            newSharingWith = [...task.sharingWith];
            if (!newSharingWith.includes(newClientEmail)) {
              newSharingWith.push(newClientEmail);
            }
          } else {
            newSharingWith = task?.sharingWith?.map((email) =>
              email === originalClientEmail ? newClientEmail : email
            );
          }
          let partyRoleCamel = _.camelCase(clientForm.role);
          const taskRef = doc(db, "tasks", task.id);
          batch.update(taskRef, {
            sharingWith: newSharingWith,
            [`sharingWithRole.${partyRoleCamel}`]: newClientEmail,
          });
        }
      });
    }
  }
  return batch.commit();
}

export function updateTransFieldsInDb(transactionId, transactionFields) {
  const docRef = doc(db, "transactions", transactionId);
  return updateDoc(docRef, {
    ...transactionFields,
    updatedAt: serverTimestamp(),
  });
}

export function updateTransUpdatedInDb(transactionId) {
  const docRef = doc(db, "transactions", transactionId);
  return updateDoc(docRef, {
    updatedAt: serverTimestamp(),
  });
}

export async function updateTransAddSharingInDb(docId, party, transaction) {
  let partyRoleCamel = _.camelCase(party.role);
  const docRef = doc(db, "transactions", docId);
  let emailsToAdd = [];

  // Always add the main party email
  if (party.email) {
    emailsToAdd.push(party.email);
  }

  let newFields = {
    [`sharingWithRole.${partyRoleCamel}`]: party.email,
    mlsNumbers: transaction.mlsNumbers || [],
  };

  // Add transaction coordinator email if applicable
  if (
    partyIsAgent(party.role) &&
    party.hasTransactionCoordinator &&
    party.transactionCoordinator?.email
  ) {
    newFields[`sharingWithRole.otherTransactionCoordinator`] =
      party.transactionCoordinator.email;
    emailsToAdd.push(party.transactionCoordinator.email);
  }

  // Add co-agent email if applicable
  if (partyIsAgent(party.role) && party.hasCoAgent && party.coAgent?.email) {
    newFields[`sharingWithRole.otherCoAgent`] = party.coAgent.email;
    emailsToAdd.push(party.coAgent.email);
  }

  // Add assistant email if applicable
  if (party.hasAssistant && party.assistant?.email) {
    newFields[`sharingWithRole.${partyRoleCamel}Assistant`] = party.assistant.email;
    emailsToAdd.push(party.assistant.email);
  }

  // Only add emails that exist to the sharingWith array
  if (emailsToAdd.length > 0) {
    newFields["sharingWith"] = arrayUnion(...emailsToAdd);
  }

  return updateDoc(docRef, newFields);
}

export async function updateTransRemoveSharingInDb(transactionId, party, transaction) {
  let partyRoleCamel = _.camelCase(party.role);
  if (!transactionId || !party?.email) {
    return;
  }
  const docRef = doc(db, "transactions", transactionId);
  let emailParty = party.email || "";
  let emailTc = party.transactionCoordinator?.email || "";
  let emailCoAgent = party.coAgent?.email || "";
  let emailAssistant = party.assistant?.email || "";
  let emailsToRemove = [];

  // Always add the main party email if it exists
  if (emailParty) {
    emailsToRemove.push(emailParty);
  }

  let newFields = {
    [`sharingWithRole.${partyRoleCamel}`]: "",
    mlsNumbers: transaction.mlsNumbers || [],
  };

  // Handle Transaction Coordinator removal
  if (
    partyIsAgent(party.role) &&
    party.hasTransactionCoordinator &&
    party.transactionCoordinator?.email?.length > 0
  ) {
    newFields[`sharingWithRole.otherTransactionCoordinator`] = "";
    if (emailTc) {
      emailsToRemove.push(emailTc);
    }
  }

  // Handle CoAgent removal
  if (partyIsAgent(party.role) && party.hasCoAgent && party.coAgent?.email?.length > 0) {
    newFields[`sharingWithRole.otherCoAgent`] = "";
    if (emailCoAgent) {
      emailsToRemove.push(emailCoAgent);
    }
  }

  // Handle Assistant removal
  if (party.hasAssistant && party.assistant?.email?.length > 0) {
    newFields[`sharingWithRole.${partyRoleCamel}Assistant`] = "";
    if (emailAssistant) {
      emailsToRemove.push(emailAssistant);
    }
  }

  // Remove emails from sharingWith array
  if (emailsToRemove.length > 0) {
    newFields["sharingWith"] = arrayRemove(...emailsToRemove);
  }

  return updateDoc(docRef, newFields);
}

export async function deleteTransactionInDb(transactionId) {
  const batch = writeBatch(db);

  // COULD ALSO REMOVE TRANSACTION ID FROM CLIENTS
  // const tasksRef = collection(db, "tasks");
  // const queryTasks = query(
  //   tasksRef,
  //   where("transactionId", "==", transactionId)
  // );
  // const snapshotTasks = await getDocs(queryTasks);
  // snapshotTasks.forEach((task) => {
  //   batch.delete(doc(db, "tasks", task.id));
  // });

  const partiesRef = collection(db, "parties");
  const queryParties = query(
    partiesRef,
    where("transactionId", "==", transactionId)
  );
  const snapshotParties = await getDocs(queryParties);
  snapshotParties.forEach((party) => {
    batch.delete(doc(db, "parties", party.id));
  });

  // const docsRef = collection(db, "documents");
  // const queryDocs = query(docsRef, where("transactionId", "==", transactionId));
  // const snapshotDocs = await getDocs(queryDocs);
  // snapshotDocs.forEach((document) => {
  //   batch.delete(doc(db, "documents", document.id));
  //   // const annotsRef = collection(db, "documents");
  //   // const queryDocs = query(docsRef, where("transactionId", "==", transactionId));
  //   // const snapshotDocs = await getDocs(queryDocs);
  //   // snapshotDocs.forEach((document) => {
  //   //   batch.delete(doc(db, "documents", document.id));
  //   // });
  //   // deleteDocFromStorage(document.docRef);
  // });

  const transactionRef = doc(db, "transactions", transactionId);
  batch.delete(transactionRef);
  return batch.commit();
}

///////////////////////////////////////////////////////////////
/////////////////////  PARTIES  //////////////////////////
///////////////////////////////////////////////////////////////

export function fetchPartiesFromDb(transactionId) {
  let queryRef = collection(db, "parties");
  if (authClaims?.r === "a" && authClaims?.a) {
    queryRef = query(queryRef, where("userId", "in", authClaims.a));
  } else if (authClaims?.r === "t") {
    queryRef = query(queryRef, where("tcId", "==", auth.currentUser.uid));
  } else if (authClaims?.r === "m") {
    queryRef = query(queryRef, where("managerId", "==", auth.currentUser.uid));
  } else if (authClaims?.r === "g" && authClaims?.m) {
    queryRef = query(queryRef, where("managerId", "==", authClaims.m[0]));
  }
  else {
    queryRef = query(
      queryRef,
      or(
        where("userId", "==", auth.currentUser.uid),
        where("coAgentId", "==", auth.currentUser.uid),
        where("tcId", "==", auth.currentUser.uid),
        where("agentAsTcId", "==", auth.currentUser.uid)
      )
    );
  }
  return query(queryRef, where("transactionId", "==", transactionId));
}

export function fetchPartiesForUserFromDb() {   // non-user parties also have party.assistant.email etc. and so lookup as the asst works
  let queryRef = collection(db, "parties");
  return query(
    queryRef,
    or(
      where("email", "==", auth.currentUser.email),
      where("transactionCoordinator.email", "==", auth.currentUser.email),
      where("coAgent.email", "==", auth.currentUser.email),
      where("assistant.email", "==", auth.currentUser.email)
    )
  );
}

export function fetchPartiesForPartyFromDb(transactionId) {
  let queryRef = collection(db, "parties");
  return query(queryRef, where("transactionId", "==", transactionId));
}

export function fetchPartyFromDb(partyId) {
  const partyRef = doc(db, "parties", partyId);
  return partyRef;
}

export async function getPartyFromDb(partyId) {
  const partyRef = doc(db, "parties", partyId);
  const snapshot = await getDoc(partyRef);
  return snapshot.data();
}

// Helper function to check if a user is an agent with role=agent and type=user
async function isUserAgentWithCredentials(email) {
  try {
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("email", "==", email));
    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      const userData = snapshot.docs[0].data();
      return userData.role === "agent" && userData.type === "user";
    }
    return false;
  } catch (error) {
    console.error("Error checking user credentials:", error);
    return false;
  }
}
// new version of function
// only called if adding party as MY TC
async function isUserAgentOrTcWithCredentials(email) {
  try {
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("email", "==", email));
    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      const userData = snapshot.docs[0].data();
      return {
        isAgentUser: userData.role === "agent" && userData.type === "user",
        isTcUser: userData.role === "tc" && userData.type === "user" && userData.authCustomClaims && userData.authCustomClaims.r === "t",
        userIdInAuth: userData.id,
      }
    }
    else  {
      return false;
    }
  } catch (error) {
    // console.error("Error checking user credentials for TC:", error);
    return false;
  }
}

export async function addPartyToDb(party, transaction, allParties) {
  let userDetails = null;
  let _agentAsTcId = transaction.agentAsTcId || "";
  let _tcId = transaction.tcId || "";
  try {
    if (partyIsMyTc(party.role)) {
      userDetails = await isUserAgentOrTcWithCredentials(party.email);
      if (!userDetails) {
        throw new Error("Email supplied is not a TransActioner subscriber and cannot be added as a TC.\nCheck the email or contact the person to get set up correctly inside TransActioner.");
      }
      if (userDetails && userDetails.userIdInAuth && (!userDetails.isAgentUser && !userDetails.isTcUser)) {
        throw new Error("Email supplied is not an agent or TC in TransActioner and cannot be added as a TC.\nCheck the email or contact the person to get set up correctly inside TransActioner.");
      }
      if (userDetails?.isAgentUser) {
        party.isUser = true;
        party.userRoleInTransActioner = "agent";
        party.userIdInAuth = userDetails.userIdInAuth;
        _agentAsTcId = userDetails.userIdInAuth || "";
      }
      else if (userDetails?.isTcUser) {
        party.isUser = true;
        party.userRoleInTransActioner = "tc"; 
        party.userIdInAuth = userDetails.userIdInAuth;
        _tcId = userDetails.userIdInAuth || "";
      }
    }
  } catch(error)
  {
    throw error;
  }
  const partyId = doc(collection(db, "parties")).id;
  await setDoc(doc(db, "parties", partyId), {
    ...party,
    id: partyId,
    transactionId: transaction.id,
    userId: transaction.userId,
    tcId: _tcId,
    agentAsTcId: _agentAsTcId,
    managerId: transaction.managerId || "",
    coAgentId: transaction.coAgentId || "",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  // When adding agent see if already a TransActioner user and add isUser and userId to party
  // (so we know they are a user when sharing docs)
  if (partyIsAgent(party.role)) {
    functionLinkUserToParty({ partyId: partyId, party: party });
  }
  // When adding my TC, check if it's an agent acting as TC or a dedicated TC
  if (partyIsMyTc(party.role)) {          // checks if role === "TC (Mine)"
    if (userDetails && userDetails.isAgentUser) {
      // Use agent-as-TC function for agents with role=agent and type=user
      functionAddAgentAsTcToTransaction({ party: party });
    } else if (userDetails && userDetails.isTcUser) {
      // Use regular TC function for dedicated TCs
      functionAddTcIdToTransaction({ party: party });
    }
  }
  // When adding my CoAgent "CoAgent (Mine)" add the id and other info of the CoAgent to the transaction so my CoAgent can access it
  if (partyIsMyCoAgent(party.role)) {
    userDetails = await isUserAgentOrTcWithCredentials(party.email);
    if (!userDetails || (!userDetails.isAgentUser)) {
        throw new Error("Email supplied is not a TransActioner subscriber and cannot be added as a CoAgent.\nCheck the email or contact the person to get set up correctly inside TransActioner.");
      }
    functionAddCoAgentIdToTransaction({ party: party });
  }

  // Add party to transaction's sharingWith and sharingWithRole fields
  // This ensures the party (and their assistant/TC/coAgent) can see the transaction when they log in.
  // Skip this for the other side's client (e.g. the Buyer party on a Seller's
  // transaction, or the Seller party on a Buyer's transaction) - the agent
  // doesn't represent them, so they shouldn't get portal access to the
  // transaction just because they have an email on file.
  if (!partyIsOppositeSideClient(party.role, transaction.agentRepresents)) {
    try {
      await updateTransAddSharingInDb(transaction.id, party, transaction);
    } catch (error) {
      console.log("Error adding party to transaction sharing: ", error);
      // Don't throw - party was created successfully, sharing update is secondary
    }
  }

  return partyId;
}

export async function updatePartyInDbAndSharing(party, partyFields, docs, transaction, tasks) {
  // update parties
  try {
    await updatePartyInDb(party, partyFields);
  } catch (error) {
    console.log("ERROR updating Party: ", error);
      throw error;
  }

  const partyRoleCamel = _.camelCase(partyFields.role);
  let hasNewEmail = false;
  let hasNewtc = false;
  let hasNewCoagent = false;
  let hasNewAssistant = false;
  let hasRemovedTc = (  // checkbox deselected or email removed
    (party.hasTransactionCoordinator && !partyFields.hasTransactionCoordinator)
    || (partyFields.hasTransactionCoordinator
      && !partyFields.transactionCoordinator?.email
      && party.transactionCoordinator?.email));

  let hasRemovedCoagent = (  // checkbox deselected or email removed
    (party.hasCoAgent && !partyFields.hasCoAgent)
    || (partyFields.hasCoAgent
      && !partyFields.coAgent?.email
      && party.coAgent?.email));
  let hasRemovedAssistant = (  // checkbox deselected or email removed
    (party.hasAssistant && !partyFields.hasAssistant)
    || (partyFields.hasAssistant
      && !partyFields.assistant?.email
      && party.assistant?.email));
  // let hasRemovedEmail = (party.email && !partyFields.email);

  // update transaction
  let sharingWithArray = transaction.sharingWith ? [ ...transaction.sharingWith ] : [];
  let sharingWithRole = _.cloneDeep(transaction.sharingWithRole) || {};
  // Skip granting portal access for the other side's client (e.g. the Buyer
  // party on a Seller's transaction, or the Seller party on a Buyer's
  // transaction) - the agent doesn't represent them, so they shouldn't be
  // able to view the transaction just because they have an email on file.
  const isOppositeSideClient = partyIsOppositeSideClient(
    partyFields.role || party.role,
    transaction.agentRepresents
  );
  if (!isOppositeSideClient && !sharingWithArray.includes(partyFields.email)) {
    sharingWithArray = sharingWithArray.filter(item => item !== party.email);        // remove old agent email if exists(party.email);
    sharingWithArray.push(partyFields.email);         // new email for agent
    sharingWithRole[partyRoleCamel] = partyFields.email;
    hasNewEmail = true;
  }
  if (!party.isUser) {    // agent, escrow, title, lender...

    // tc transaction coordinator
    if (hasRemovedTc) {
        sharingWithRole = _.omit(sharingWithRole, partyRoleCamel + 'TransactionCoordinator');
        sharingWithArray = sharingWithArray.filter(item => item !== (party.transactionCoordinator?.email || ""));        // remove old tc email if exists
    }
    else if (partyFields.hasTransactionCoordinator && partyFields.transactionCoordinator?.email) {    // TC
      if (!sharingWithArray.includes(partyFields.transactionCoordinator.email)) {  // new email for TC
        sharingWithArray = sharingWithArray.filter(item => item !== (party.transactionCoordinator?.email || ""));        // remove old tc email if exists
        sharingWithArray.push(partyFields.transactionCoordinator.email);    // add new tc email
        sharingWithRole[`${partyRoleCamel}TransactionCoordinator`] = partyFields.transactionCoordinator.email;
        hasNewtc = true;
      }
    }
    // coagent
    if (hasRemovedCoagent) {
      sharingWithRole = _.omit(sharingWithRole, partyRoleCamel + 'CoAgent');
      sharingWithArray = sharingWithArray.filter(item => item !== (party.coAgent?.email || ""));        // remove old coAgent email if exists
    }
    else if (partyFields.hasCoAgent && partyFields.coAgent?.email) {    // CoAgent
       if (!sharingWithArray.includes(partyFields.coAgent.email)) {  // new email for CoAgent
        sharingWithArray = sharingWithArray.filter(item => item !== (party.coAgent?.email || ""));        // remove old coAgent email if exists(party.coAgent?.email || "");        // remove old coAgent email if exists
        sharingWithArray.push(partyFields.coAgent.email);                // add new coAgent email
        sharingWithRole[`${partyRoleCamel}CoAgent`] = partyFields.coAgent.email;
        hasNewCoagent = true;
      }
    }
    // assistant
    if (hasRemovedAssistant) {
        sharingWithRole = _.omit(sharingWithRole, partyRoleCamel + 'Assistant');
        sharingWithArray = sharingWithArray.filter(item => item !== (party.assistant?.email || ""));        // remove old assistant email if exists
      }
    else if (partyFields.hasAssistant && partyFields.assistant?.email) {    // Assistant
      if (!sharingWithArray.includes(partyFields.assistant.email)) {  // new email for Assistant
        sharingWithArray = sharingWithArray.filter(item => item !== (party.assistant?.email || ""));        // remove old assistant email if exists
        sharingWithArray.push(partyFields.assistant.email);                // add new assistant email
        sharingWithRole[`${partyRoleCamel}Assistant`] = partyFields.assistant.email;
        hasNewAssistant = true;
      }
    }
  }
  try {   // outside if to update party's email change, may or may not be user
    const transactionRef = doc(db, "transactions", transaction.id);
    await updateDoc(transactionRef, {
      sharingWithRole: sharingWithRole,
      sharingWith: sharingWithArray,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.log("Error updating transaction sharing, but party updated. Error:", error);
    throw new Error("Unable to update transaction. Contact support@transactioner.com to fix.");
  }
  
  // update docs sharing
  if (hasNewEmail || hasNewtc || hasNewCoagent || hasNewAssistant || hasRemovedTc || hasRemovedCoagent || hasRemovedAssistant) {  // if no change then no need to walk through docs
    // console.log("updating docs sharing...", docs?.length);
    try {
      docs.forEach(async (document) => {
        let docsSharingWithArray = document.sharingWith ? [ ...document.sharingWith ] : [];
        let docsSharingWithRole = document.sharingWithRole ? _.cloneDeep(document.sharingWithRole) : {};
        
        if (hasRemovedTc) {
          docsSharingWithArray = docsSharingWithArray.filter(item => item !== (party.transactionCoordinator?.email || ""));        // remove old tc email if exists
          docsSharingWithRole = _.omit(docsSharingWithRole, partyRoleCamel + 'TransactionCoordinator');
        }
        if (hasRemovedCoagent) {
          docsSharingWithArray = docsSharingWithArray.filter(item => item !== (party.coAgent?.email || ""));        // remove old coAgent email if exists
          docsSharingWithRole = _.omit(docsSharingWithRole, partyRoleCamel + 'CoAgent');
        }
        if (hasRemovedAssistant) {
          docsSharingWithArray = docsSharingWithArray.filter(item => item !== (party.assistant?.email || ""));        // remove old assistant email if exists
          docsSharingWithRole = _.omit(docsSharingWithRole, partyRoleCamel + 'Assistant');
        }
        // make sure agent has access to this doc b4 sharing with tc
        if (document.sharingWithRole?.[partyRoleCamel] === party.email) {  
          if (hasNewEmail) {
            docsSharingWithArray = docsSharingWithArray.filter(item => item !== party.email);        // remove old agent email if exists(party.email);
            docsSharingWithArray.push(partyFields.email);         // new email for agent
            docsSharingWithRole[partyRoleCamel] = partyFields.email;
          }
          if (hasNewtc) {
            docsSharingWithArray = docsSharingWithArray.filter(item => item !== party.transactionCoordinator?.email);        // remove old tc email if exists
            docsSharingWithArray.push(partyFields.transactionCoordinator.email);    // add new tc email
            docsSharingWithRole[`${partyRoleCamel}TransactionCoordinator`] = partyFields.transactionCoordinator.email;
          }
          if (hasNewCoagent) {
            docsSharingWithArray = docsSharingWithArray.filter(item => item !== (party.coAgent?.email || ""));        // remove old coAgent email if exists
            docsSharingWithArray.push(partyFields.coAgent.email);                // add new coAgent email
            docsSharingWithRole[`${partyRoleCamel}CoAgent`] = partyFields.coAgent.email;
          }
          if (hasNewAssistant) {
            docsSharingWithArray = docsSharingWithArray.filter(item => item !== (party.assistant?.email || ""));        // remove old assistant email if exists
            docsSharingWithArray.push(partyFields.assistant.email);                // add new assistant email
            docsSharingWithRole[`${partyRoleCamel}Assistant`] = partyFields.assistant.email;
          }

          const docRef = doc(db, "documents", document.id);
          await updateDoc(docRef, {
            sharingWithRole: docsSharingWithRole,
            sharingWith: docsSharingWithArray,
            updatedAt: serverTimestamp(),
          });
        }
      });
    } catch (error) {
      console.log("Error updating docs sharing, but party updated. Error:", error);
      throw new Error("Unable to share documents to new emails for party contact. Either reshare documents or contact support@transactioner.com to fix.");
    }
    // tasks
    try {
      // console.log("updating tasks sharing...", tasks?.length);
      
      tasks.forEach(async (task) => {
        let tasksSharingWithArray = task.sharingWith ? [ ...task.sharingWith ] : [];
        let tasksSharingWithRole = task.sharingWithRole ? _.cloneDeep(task.sharingWithRole) : {};
        
        if (hasRemovedTc) {
          tasksSharingWithArray = tasksSharingWithArray.filter(item => item !== (party.transactionCoordinator?.email || ""));        // remove old tc email if exists
          tasksSharingWithRole = _.omit(tasksSharingWithRole, partyRoleCamel + 'TransactionCoordinator');
        }
        if (hasRemovedCoagent) {
          tasksSharingWithArray = tasksSharingWithArray.filter(item => item !== (party.coAgent?.email || ""));        // remove old coAgent email if exists
          tasksSharingWithRole = _.omit(tasksSharingWithRole, partyRoleCamel + 'CoAgent');
        } 
        if (hasRemovedAssistant) {
          tasksSharingWithArray = tasksSharingWithArray.filter(item => item !== (party.assistant?.email || ""));        // remove old assistant email if exists
          tasksSharingWithRole = _.omit(tasksSharingWithRole, partyRoleCamel + 'Assistant');
        }
        if (task.sharingWithRole?.[partyRoleCamel] === party.email) {  // make sure agent has access to this task b4 sharing with tc
          if (hasNewEmail) {
            tasksSharingWithArray = tasksSharingWithArray.filter(item => item !== party.email);        // remove old agent email if exists(party.email);
            tasksSharingWithArray.push(partyFields.email);         // new email for agent
            tasksSharingWithRole[partyRoleCamel] = partyFields.email;
          }
          if (hasNewtc) {
            tasksSharingWithArray = tasksSharingWithArray.filter(item => item !== (party.transactionCoordinator?.email || ""));        // remove old tc email if exists
            tasksSharingWithArray.push(partyFields.transactionCoordinator.email);    // add new tc email
            tasksSharingWithRole[`${partyRoleCamel}TransactionCoordinator`] = partyFields.transactionCoordinator.email;
          }
          if (hasNewCoagent) {
            tasksSharingWithArray = tasksSharingWithArray.filter(item => item !== (party.coAgent?.email || ""));        // remove old coAgent email if exists
            tasksSharingWithArray.push(partyFields.coAgent.email);                // add new coAgent email
            tasksSharingWithRole[`${partyRoleCamel}CoAgent`] = partyFields.coAgent.email;
          }
          if (hasNewAssistant) {
            tasksSharingWithArray = tasksSharingWithArray.filter(item => item !== (party.assistant?.email || ""));        // remove old assistant email if exists
            tasksSharingWithArray.push(partyFields.assistant.email);                // add new assistant email
            tasksSharingWithRole[`${partyRoleCamel}Assistant`] = partyFields.assistant.email;
          }
          const taskRef = doc(db, "tasks", task.id);
          await updateDoc(taskRef, {
            sharingWithRole: tasksSharingWithRole,
            sharingWith: tasksSharingWithArray,
            updatedAt: serverTimestamp(),
          });
        }
      });
    } catch (error) {
      console.log("Error updating tasks sharing, but party updated. Error:", error);
      throw new Error("Unable to share tasks to new emails for party contact. Either reshare tasks or contact support@transactioner.com to fix.");
    }
  }
}

export async function updatePartyInDb(party, partyFields) {
  const docRef = doc(db, "parties", party.id);
  updateDoc(docRef, {
    ...partyFields,
    updatedAt: serverTimestamp(),
  });
  if (partyIsAgent(party.role)) {
    functionLinkUserToParty({ partyId: party.id, party: partyFields });
  }
  // Not needed since we no longer allow emails to be changed
  // if (partyIsMyTc(partyFields.role) && party.email !== partyFields.email) {
  //   functionAddTcIdToTransaction({ party: partyFields });
  // }
  // When editing my CoAgent "CoAgent (Mine)", update the transaction.coAgent field
  if (partyIsMyCoAgent(partyFields.role)) {
    functionAddCoAgentIdToTransaction({ party: partyFields });
  }
  return;
}

export function updateListingAgentPartyInDb(transaction, mlsData, parties, docs, tasks, allParties) {
  if (transaction.agentRepresents !== "Buyer") {
    return;
  }
  if (!mlsData) {
    return;
  }
  if (!mlsData.listingAgent) {
    return;
  }

  const existingListingAgentParty = parties?.filter(
    (party) => party.role === "Listing Agent"
  )?.[0];
  if (existingListingAgentParty?.id) {
    try {
      deletePartyInDb(existingListingAgentParty, transaction, docs, tasks, allParties);
    } catch (error) {
      console.log("Problem deleting LA, igorning and carry on");
    }
  }
  if (mlsData.listingAgent.email) {
    mlsData.listingAgent.email = mlsData.listingAgent.email.toLowerCase();
  } else {
    mlsData.listingAgent.email = "";
  }

  addPartyToDb(
    {
      ...mlsData.listingAgent,
      type: "Party",
      role: "Listing Agent",
    },
    transaction
  );

  return;
}

export function updateOwnersPartyInDb(transaction, parties) {
  if (!transaction) {
    return;
  }
  if (transaction.agentRepresents !== "Buyer") {
    return;
  }
  if (!(transaction.owner?.entityName || transaction.owner?.lastName)) {
    return;
  }

  const ownerParty = parties?.filter((party) => party.role === "Seller")[0];
  const owner2Party = parties?.filter((party) => party.role === "Seller2")[0];

  if (ownerParty) {
    updatePartyInDb(ownerParty, transaction.owner);
  } else {
    try {
      const owner1PartyId = doc(collection(db, "parties")).id;
      addPartyToDb(
        {
          ...transaction.owner,
          id: owner1PartyId,
          type: "Party",
          role: "Seller",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        transaction
      );
    } catch (error) {
      console.log("Error adding owner1 ", error.message);
    }
  }

  if (transaction.owner2?.entityName || transaction.owner2?.lastName) {
    if (owner2Party) {
      updatePartyInDb(owner2Party, transaction.owner2);
    } else {
      try {
        console.log(
          "No 2nd owner so create a new party:::",
          transaction.owner2
        );
        const owner2PartyId = doc(collection(db, "parties")).id;
        addPartyToDb(
          {
            ...transaction.owner2,
            id: owner2PartyId,
            type: "Party",
            role: "Seller 2",
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          transaction
        );
      } catch (error) {
        console.log("Error adding owner2 ", error.message);
      }
    }
  }
  return;
}
/** allParties is optional, only needed for Listing Agent and Buyer Agent so that deletions of agents also remove sharing with clients
 */
export function deletePartyInDb(party, transaction, docs, tasks, allParties = null) {
  const docRef = doc(db, "parties", party.id);
  deleteDoc(docRef);
  if (partyIsMyTc(party.role)) {
    try {
      isUserAgentWithCredentials(party.email).then(
        (isAgentUser) => {
          if (isAgentUser) {
            functionRemoveAgentAsTcFromTransaction({ party: party });
          } else {
            functionRemoveTcIdFromTransaction({ party: party });
          }
        }
      )
    } catch (error) {
      console.log("Error removing TC from transaction ", error.message);
    }
  }
  if (partyIsMyCoAgent(party.role)) {
    try {
      functionRemoveCoAgentIdFromTransaction({ party: party });
    } catch (error) {
      console.log("Error removing CoAgent from transaction ", error.message);
    }
  }
  try {
    updateTransRemoveSharingInDb(party.transactionId, party, transaction);
  } catch (error) {
    console.log("Error removing sharing from transaction ", error.message);
  }
  try {
    docs && Array.isArray(docs) && docs.forEach(doc => {
      updateDocRemoveSharingInDb(doc.id, party, transaction, null, (doc.sharedLinkedDocId ? doc.sharedLinkedDocId : null));
      // If this is an agent being unshared, also remove sharing with their clients
  if (partyIsAgent(party.role) && (party.role === "Buyer Agent" || party.role === "Listing Agent") && !party.isUser && allParties) {
    const agentClients = getAgentClientParties(party, allParties, transaction);
    let newFields = {};
    let emailsToRemove = [];
    agentClients.forEach(client => {
      if (client.email) {
        const clientRoleCamel = _.camelCase(client.role);
        newFields[`sharingWithRole.${clientRoleCamel}`] = "";
        emailsToRemove.push(client.email);
      }
    });
    newFields["sharingWith"] = arrayRemove(...emailsToRemove);
    updateDocInDb(doc.id, newFields);
  }
    });
  } catch (error) {
    console.log("Error removing sharing from docs ", error.message);
  }
  try {
  tasks && tasks.forEach(task => {
    updateTaskRemoveSharingInDb(task.id, party, transaction);
  });
    } catch (error) {
    console.log("Error removing sharing from tasks ", error.message);
  }
  return;
}

/**
 * Delete a client party who shares an email with the primary client.
 * This function only deletes the party document and clears the sharingWithRole
 * for the deleted role, but does NOT remove the email from sharingWith arrays
 * since the primary client still uses that email.
 */
export async function deleteSharedEmailClientInDb(party, transaction, docs, tasks) {
  // Delete the party document
  const partyRef = doc(db, "parties", party.id);
  await deleteDoc(partyRef);

  // Clear sharingWithRole for this party's role in transaction (but don't remove email from sharingWith)
  const partyRoleCamel = _.camelCase(party.role);
  const transRef = doc(db, "transactions", transaction.id);
  await updateDoc(transRef, {
    [`sharingWithRole.${partyRoleCamel}`]: "",
    updatedAt: serverTimestamp(),
  });

  // Clear sharingWithRole for this party's role in each document
  // Also remove party.email from sharingWith IF no other sharingWithRole entry uses that email
  if (docs && Array.isArray(docs)) {
    for (const document of docs) {
      const docRef = doc(db, "documents", document.id);

      // Check if party.email is used by any other role in sharingWithRole (after clearing this role)
      const sharingWithRole = document.sharingWithRole || {};
      let emailStillNeeded = false;

      for (const [role, email] of Object.entries(sharingWithRole)) {
        // Skip the role we're about to clear
        if (role === partyRoleCamel) continue;
        // Check if any other role uses this email
        if (email === party.email) {
          emailStillNeeded = true;
          break;
        }
      }

      const updateFields = {
        [`sharingWithRole.${partyRoleCamel}`]: "",
        updatedAt: serverTimestamp(),
      };

      // Only remove email from sharingWith if no other role needs it
      if (!emailStillNeeded) {
        updateFields["sharingWith"] = arrayRemove(party.email);
      }

      await updateDoc(docRef, updateFields);
    }
  }

  // Clear sharingWithRole for this party's role in each task (but don't remove email from sharingWith)
  if (tasks && Array.isArray(tasks)) {
    for (const task of tasks) {
      if (task.sharingWithRole && task.sharingWithRole[partyRoleCamel]) {
        const taskRef = doc(db, "tasks", task.id);
        await updateDoc(taskRef, {
          [`sharingWithRole.${partyRoleCamel}`]: "",
          updatedAt: serverTimestamp(),
        });
      }
    }
  }

  return;
}

///////////////////////////////////////////////////////////////
/////////////////////////  PEOPLE  ////////////////////////////
///////////////////////////////////////////////////////////////

export function fetchPeopleFromDb() {
  const peopleRef = collection(db, "people");
  return query(
    peopleRef,
    where("userId", "==", auth.currentUser.uid),
    orderBy("updatedAt", "desc")
  );
}

export function addPersonToDb(person) {
  const user = auth.currentUser;
  const peopleRef = collection(db, "people");
  return addDoc(peopleRef, {
    ...person,
    userId: user.uid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export function updatePersonInDb(personId, personFields) {
  const docRef = doc(db, "people", personId);
  return updateDoc(docRef, {
    ...personFields,
    updatedAt: serverTimestamp(),
  });
}

export function deletePersonInDb(personId) {
  const docRef = doc(db, "people", personId);
  return deleteDoc(docRef);
}

///////////////////////////////////////////////////////////////
/////////////////////////  TASKS  /////////////////////////////
///////////////////////////////////////////////////////////////

export function fetchTasksUpcomingFromDb(id = null) {
  let queryRef = query(collection(db, "tasks"));
  if (authClaims?.r === "a" && authClaims?.a) {
    queryRef = query(queryRef, where("userId", "in", authClaims.a));
  } else if (authClaims?.r === "t") {
    // queryRef = query(queryRef, where("accessIds", "array-contains", auth.currentUser.uid));
    queryRef = query(queryRef, where("tcId", "==", auth.currentUser.uid));
  } else if (authClaims?.r === "g" && authClaims?.m) {
if (id) {
      queryRef = query(queryRef,
        where("userId", "==", id),
        where("managerId", "==", authClaims.m[0]));
    }
    else {
      queryRef = query(queryRef,
        where("managerId", "==", authClaims.m[0]));
    }
  }
  else if (id) {
    queryRef = query(
      queryRef,
      where("userId", "==", id),
      where("managerId", "==", auth.currentUser.uid)
    );
  } else {
    queryRef = query(
      queryRef,
      or(
        where("userId", "==", auth.currentUser.uid),
        where("coAgentId", "==", auth.currentUser.uid),
        where("tcId", "==", auth.currentUser.uid),
        where("agentAsTcId", "==", auth.currentUser.uid)
      )
    );
  }
  return query(
    queryRef,
    where("end", ">=", startOfDay(subDays(new Date(), 1))),
    orderBy("end"),
    limit(400)
  );
}

export function fetchTasksTransFromDb(transactionId) {
  let queryRef = collection(db, "tasks");
  if (authClaims?.r === "a" && authClaims?.a) {
    queryRef = query(queryRef, where("userId", "in", authClaims.a));
  } else if (authClaims?.r === "t") {
    queryRef = query(queryRef, where("tcId", "==", auth.currentUser.uid));
  } else if (authClaims?.r === "m") {
    queryRef = query(queryRef, where("managerId", "==", auth.currentUser.uid));
  } else if (authClaims?.r === "g" && authClaims?.m) {
    queryRef = query(queryRef, where("managerId", "==", authClaims.m[0]));
  }
  else {
    queryRef = query(
      queryRef,
      or(
        where("userId", "==", auth.currentUser.uid),
        where("coAgentId", "==", auth.currentUser.uid),
        where("tcId", "==", auth.currentUser.uid),
        where("agentAsTcId", "==", auth.currentUser.uid)
      )
    );
  }
  return query(
    queryRef,
    where("transactionId", "==", transactionId),
    orderBy("end")
  );
}

export function fetchAllTasksFromDb() {
  let queryRef = query(collection(db, "tasks"));

  // Apply user/role filters
  if (authClaims?.r === "a" && authClaims?.a) {
    queryRef = query(queryRef, where("userId", "in", authClaims.a));
  } else if (authClaims?.r === "t") {
    queryRef = query(queryRef, where("tcId", "==", auth.currentUser.uid));
  } else if (authClaims?.r === "g" && authClaims?.m) {
    queryRef = query(queryRef, where("managerId", "==", authClaims.m[0]));
  } else {
    queryRef = query(
      queryRef,
      or(
        where("userId", "==", auth.currentUser.uid),
        where("coAgentId", "==", auth.currentUser.uid),
        where("tcId", "==", auth.currentUser.uid),
        where("agentAsTcId", "==", auth.currentUser.uid)
      )
    );
  }

  return queryRef;
}

export function fetchTasksForPartyFromDb(party) {
  // Defensive check for undefined party
  if (!party || !party.transactionId) {
    console.warn("fetchTasksForPartyFromDb called with invalid party:", party);
    // Return an empty query that will return no results
    const tasksRef = collection(db, "tasks");
    return query(tasksRef, where("id", "==", "__INVALID_PARTY__"));
  }
  // const partyRoleCamel = _.camelCase(party.role);
  const docsRef = collection(db, "tasks");
  return query(
    docsRef,
    where("transactionId", "==", party.transactionId),
    where("sharingWith", "array-contains", auth.currentUser.email)
    // where(`sharingWithRole.${partyRoleCamel}`, "==", party.email)
  );
}

export function addTaskToDb(task, transaction) {
  const tasksRef = collection(db, "tasks");
  return addDoc(tasksRef, {
    ...task,
    status: "Active",
    userId: transaction.userId,
    transactionId: transaction.id,
    transactionTitle: transaction.title,
    archived: false,
    tcId: transaction.tcId || "",
    agentAsTcId: transaction.agentAsTcId || "",
    managerId: transaction.managerId || "",
    coAgentId: transaction.coAgentId || "",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export function addTasksFromDocToDb(tasks, existingTasks, transaction) {
  const batch = writeBatch(db);
  tasks.forEach((task) => {
    const foundTask = existingTasks?.find(
      (taskitem) => taskitem.title === task.title
    );
    const taskId = foundTask ? foundTask.id : doc(collection(db, "tasks")).id;
    const isExistingTask = !!foundTask;

    const taskData = {
      ...task,
      id: taskId,
      start: task.end,
      userId: transaction.userId,
      tcId: transaction.tcId || "",
      agentAsTcId: transaction.agentAsTcId || "",
      managerId: transaction.managerId || "",
      coAgentId: transaction.coAgentId || "",
      updatedAt: serverTimestamp(),
    };

    if (isExistingTask) {
      // For existing tasks, update fields but preserve createdAt
      batch.set(doc(db, "tasks", taskId), taskData, { merge: true });
    } else {
      // For new tasks, set createdAt
      batch.set(doc(db, "tasks", taskId), {
        ...taskData,
        createdAt: serverTimestamp(),
      });
    }
  });
  return batch.commit();
}

export function updateTaskInDb(taskId, taskFields) {
  const docRef = doc(db, "tasks", taskId);
  return updateDoc(docRef, {
    ...taskFields,
    updatedAt: serverTimestamp(),
  });
}

export async function getTasksForTransaction(transactionId) {
  return new Promise(async function (resolve, reject) {
    onSnapshot(fetchTasksTransFromDb(transactionId), (snapshot) => {
      const tasks = snapshot.docs.map((doc) => dataFromSnapshot(doc));
      resolve(tasks);
    });
  });
}

export async function updateTaskAddSharingInDb(taskId, party, transaction) {
  let partyRoleCamel = _.camelCase(party.role);
  const taskRef = doc(db, "tasks", taskId);
  let emailsToAdd = [];

  // Always add the main party email
  if (party.email) {
    emailsToAdd.push(party.email);
  }

  let newFields = {
    [`sharingWithRole.${partyRoleCamel}`]: party.email,
    mlsNumbers: transaction.mlsNumbers || [],
  };

  // Add transaction coordinator email if applicable
  if (
    partyIsAgent(party.role) &&
    party.hasTransactionCoordinator &&
    party.transactionCoordinator?.email
  ) {
    newFields[`sharingWithRole.otherTransactionCoordinator`] =
      party.transactionCoordinator.email;
    emailsToAdd.push(party.transactionCoordinator.email);
  }

  // Add co-agent email if applicable
  if (partyIsAgent(party.role) && party.hasCoAgent && party.coAgent?.email) {
    newFields[`sharingWithRole.otherCoAgent`] = party.coAgent.email;
    emailsToAdd.push(party.coAgent.email);
  }

  // Add assistant email if applicable
  if (party.hasAssistant && party.assistant?.email) {
    newFields[`sharingWithRole.${partyRoleCamel}Assistant`] = party.assistant.email;
    emailsToAdd.push(party.assistant.email);
  }

  // Only add emails that exist to the sharingWith array
  if (emailsToAdd.length > 0) {
    newFields["sharingWith"] = arrayUnion(...emailsToAdd);
  }

  return updateDoc(taskRef, newFields);
}

export async function updateTaskRemoveSharingInDb(docId, party, transaction) {
  if (!docId || !party?.email) {
    console.log("no id or email", docId, party?.email);
    return;
  }
  let partyRoleCamel = _.camelCase(party.role);
  const docRef = doc(db, "tasks", docId);
  let emailParty = party.email || "";
  let emailTc = party.transactionCoordinator?.email || "";
  let emailCoAgent = party.coAgent?.email || "";
  let emailAssistant = party.assistant?.email || "";
  let emailsToRemove = [];

  // Always add the main party email if it exists
  if (emailParty) {
    emailsToRemove.push(emailParty);
  }

  let newFields = {
    [`sharingWithRole.${partyRoleCamel}`]: "",
    mlsNumbers: transaction.mlsNumbers || [],
  };

  // Handle Transaction Coordinator removal
  if (
    partyIsAgent(party.role) &&
    party.hasTransactionCoordinator &&
    party.transactionCoordinator?.email
  ) {
    newFields[`sharingWithRole.otherTransactionCoordinator`] = "";
    if (emailTc) {
      emailsToRemove.push(emailTc);
    }
  }

  // Handle CoAgent removal
  if (partyIsAgent(party.role) && party.hasCoAgent && party.coAgent?.email) {
    newFields[`sharingWithRole.otherCoAgent`] = "";
    if (emailCoAgent) {
      emailsToRemove.push(emailCoAgent);
    }
  }

  // Handle Assistant removal
  if (party.hasAssistant && party.assistant?.email) {
    newFields[`sharingWithRole.${partyRoleCamel}Assistant`] = "";
    if (emailAssistant) {
      emailsToRemove.push(emailAssistant);
    }
  }

  // Remove emails from sharingWith array
  if (emailsToRemove.length > 0) {
    newFields["sharingWith"] = arrayRemove(...emailsToRemove);
  }

  return updateDoc(docRef, newFields);
}

export async function archiveTasksFromDocInDb(docId) {
  const user = auth.currentUser;
  const q = query(
    collection(db, "tasks"),
    where("docId", "==", docId),
    where("userId", "==", user.uid)
  );
  const snapshot = await getDocs(query(q));
  snapshot.forEach((doc) => {
    updateTaskInDb(doc.id, { status: "Archived" });
  });
  return;
}

export async function deleteTasksFromDocInDb(docId) {
  const user = auth.currentUser;
  const q = query(
    collection(db, "tasks"),
    where("docId", "==", docId),
    where("userId", "==", user.uid)
  );
  const snapshot = await getDocs(query(q));
  if (snapshot.exists) {
    snapshot.forEach((doc) => {
      deleteTaskInDb(doc.id);
    });
  }
  return;
}

export function deleteTaskInDb(taskId) {
  const docRef = doc(db, "tasks", taskId);
  return deleteDoc(docRef);
}

///////////////////////////////////////////////////////////////
//////////////////////  DOCUMENTS  ////////////////////////////
///////////////////////////////////////////////////////////////

export function fetchDocsUpcomingFromDb(id) {
  let queryRef = query(collection(db, "documents"));
  if (authClaims?.r === "a" && authClaims?.a) {
    queryRef = query(queryRef, where("userId", "in", authClaims.a));
  } else if (authClaims?.r === "t") {
    queryRef = query(queryRef, where("tcId", "==", auth.currentUser.uid));
  } else if (authClaims?.r === "g" && authClaims?.m) {
    if (id) {
      queryRef = query(queryRef,
        where("userId", "==", id),
        where("managerId", "==", authClaims.m[0]));
    }
    else {
      queryRef = query(queryRef,
        where("managerId", "==", authClaims.m[0]));
    }
  }
  else if (id) {
    queryRef = query(
      queryRef,
      where("userId", "==", id),
      where("managerId", "==", auth.currentUser.uid)
    );
  } else {
    queryRef = query(
      queryRef,
      or(
        where("userId", "==", auth.currentUser.uid),
        where("coAgentId", "==", auth.currentUser.uid),
        where("tcId", "==", auth.currentUser.uid),
        where("agentAsTcId", "==", auth.currentUser.uid)
      )
    );
  }
  return query(
    queryRef,
    where("status", "==", "Awaiting Signature"),
    where("updatedAt", ">", startOfDay(subDays(new Date(), 30))),
    orderBy("updatedAt", "desc")
  );
}

export function fetchDocsWithUnresolvedCommentsFromDb(id) {
  let queryRef = query(collection(db, "documents"));
  if (authClaims?.r === "a" && authClaims?.a) {
    queryRef = query(queryRef, where("userId", "in", authClaims.a));
  } else if (authClaims?.r === "t") {
    queryRef = query(queryRef, where("tcId", "==", auth.currentUser.uid));
  } else if (authClaims?.r === "g" && authClaims?.m) {
    if (id) {
      queryRef = query(queryRef,
        where("userId", "==", id),
        where("managerId", "==", authClaims.m[0]));
    }
    else {
      queryRef = query(queryRef,
        where("managerId", "==", authClaims.m[0]));
    }
  }
  else if (id) {
    queryRef = query(
      queryRef,
      where("userId", "==", id),
      where("managerId", "==", auth.currentUser.uid)
    );
  } else {
    queryRef = query(
      queryRef,
      or(
        where("userId", "==", auth.currentUser.uid),
        where("coAgentId", "==", auth.currentUser.uid),
        where("tcId", "==", auth.currentUser.uid),
        where("agentAsTcId", "==", auth.currentUser.uid)
      )
    );
  }
  return query(
    queryRef,
    where("unresolvedCommentCount", ">", 0),
    orderBy("unresolvedCommentCount", "desc")
  );
}

export function fetchDocsTransFromDb(transactionId) {
  let queryRef = collection(db, "documents");
  if (authClaims?.r === "a" && authClaims?.a) {
    queryRef = query(queryRef, where("userId", "in", authClaims.a));
  } else if (authClaims?.r === "t") {
    queryRef = query(queryRef, where("tcId", "==", auth.currentUser.uid));
  } else if (authClaims?.r === "m") {
    queryRef = query(queryRef, where("managerId", "==", auth.currentUser.uid));
  } else if (authClaims?.r === "g" && authClaims?.m) {
    queryRef = query(queryRef, where("managerId", "==", authClaims.m[0]));
  }
  else {
    queryRef = query(
      queryRef,
      or(
        where("userId", "==", auth.currentUser.uid),
        where("coAgentId", "==", auth.currentUser.uid),
        where("tcId", "==", auth.currentUser.uid),
        where("agentAsTcId", "==", auth.currentUser.uid)
      )
    );
  }
  return query(
    queryRef,
    where("transactionId", "==", transactionId),
    orderBy("updatedAt", "desc")
  );
}

export function fetchDocFromDb(docId) {
  const docRef = doc(db, "documents", docId);
  return docRef;
}

export function fetchDocsToSignFromDb(docList) {
  const docsRef = collection(db, "documents");
  return query(docsRef, where("id", "in", docList));
}

export function fetchDocsForPartyFromDb(party) {
  // Defensive check for undefined party
  if (!party || !party.transactionId) {
    console.warn("fetchDocsForPartyFromDb called with invalid party:", party);
    // Return an empty query that will return no results
    const docsRef = collection(db, "documents");
    return query(docsRef, where("id", "==", "__INVALID_PARTY__"));
  }
  // const partyRoleCamel = _.camelCase(party.role);
  const docsRef = collection(db, "documents");
  return query(
    docsRef,
    where("transactionId", "==", party.transactionId),
    where("sharingWith", "array-contains", auth.currentUser.email)
    // where(`sharingWithRole.${partyRoleCamel}`, "==", party.email)
  );
}

export function fetchDocsNeedingSignatureForUserFromDb() {
  const docsRef = collection(db, "documents");
  return query(
    docsRef,
    where("signingRequestedFor", "array-contains", auth.currentUser.email)
  );
}

// Full document records currently shared with this user, across all of
// their transactions - used to power the party Dashboard's "New Docs" card
// (needs real doc fields like docRef/filetypeMIME/linkUrl for view/download).
export function fetchSharedDocsForUserFromDb() {
  const docsRef = collection(db, "documents");
  return query(
    docsRef,
    where("sharingWith", "array-contains", auth.currentUser.email)
  );
}

// "shared" history entries addressed to this user, across all of their
// transactions — used to power the party Dashboard's "New Orders" card
// (recently-shared purchase offer contracts). See addHistoryToDb's toEmail.
export function fetchDocSharedHistoryForUserFromDb() {
  const historyRef = collection(db, "history");
  return query(
    historyRef,
    where("toEmail", "==", auth.currentUser.email),
    where("action", "==", "shared")
  );
}

export function addDocToDb(document, docId) {
  const docRef = doc(db, "documents", docId);
  return setDoc(docRef, {
    ...document,
    id: docId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function addUploadedDocToDb(
  transaction,
  docPath,
  docId,
  name,
  filetypeMIME,
  filesize,
  fileUrl
) {
  const period = name.lastIndexOf(".");
  const nameWithoutExtension = name.substring(0, period);
  const type = name.substring(period + 1);
  const docRef = doc(db, "documents", docId);
  return setDoc(docRef, {
    annotsInProgressSuggestedAdded: true,
    annotsToSign: {},
    annotsByAgent: {},
    mlsNumber: transaction.mlsNumber || "",
    userId: transaction.userId,
    tcId: transaction.tcId || "",
    agentAsTcId: transaction.agentAsTcId || "",
    managerId: transaction.managerId || "",
    coAgentId: transaction.coAgentId || "",
    hasFormFieldValues: false,
    hasFormFieldValuesForCopy: false,
    userEmail: transaction.agentEmail || transaction.agentProfile?.email || "",
    userFullname:
      transaction.agentName ||
      transaction.agentProfile?.firstName +
        " " +
        transaction.agentProfile?.lastName ||
      "",
    userFirstName:
      transaction.agentFirstName || transaction.agentProfile?.firstName || "",
    userLastName:
      transaction.agentLastName || transaction.agentProfile?.lastName || "",
    docRef: docPath,
    signed: false,
    transactionId: transaction.id,
    transactionTitle: transaction.title,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    title: nameWithoutExtension,
    name: nameWithoutExtension,
    filetype: type,
    filetypeMIME: filetypeMIME,
    filesize: filesize,
    fileUrl: fileUrl,
    status: "In Progress",
    pdfBurnVersion: 2,
    hasSigningVerification: true,
  });
}

export async function addLinkDocToDb(transaction, linkUrl, name) {
  const docId = doc(collection(db, "documents")).id;
  const docRef = doc(db, "documents", docId);
  return setDoc(docRef, {
    id: docId,
    isLink: true,
    linkUrl: linkUrl,
    filetype: "link",
    title: name,
    name: name,
    status: "In Progress",
    mlsNumber: transaction.mlsNumber || "",
    userId: transaction.userId,
    tcId: transaction.tcId || "",
    agentAsTcId: transaction.agentAsTcId || "",
    managerId: transaction.managerId || "",
    coAgentId: transaction.coAgentId || "",
    userEmail: transaction.agentEmail || transaction.agentProfile?.email || "",
    userFullname:
      transaction.agentName ||
      transaction.agentProfile?.firstName +
        " " +
        transaction.agentProfile?.lastName ||
      "",
    userFirstName:
      transaction.agentFirstName || transaction.agentProfile?.firstName || "",
    userLastName:
      transaction.agentLastName || transaction.agentProfile?.lastName || "",
    transactionId: transaction.id,
    transactionTitle: transaction.title,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function updateDocInDb(docId, documentFields, noUpdatedAt = null) {
  const docRef = doc(db, "documents", docId);
  if (noUpdatedAt) {
    return updateDoc(docRef, {
      ...documentFields,
    });
  } else {
    return updateDoc(docRef, {
      ...documentFields,
      updatedAt: serverTimestamp(),
    });
  }
}

export async function updateDocAddSharingInDb(docId, party, transaction, allParties = null, restoreAgentClients = false, sharedLinkedDocId = null) {
  let partyRoleCamel = _.camelCase(party.role);
  const docRef = doc(db, "documents", docId);
  let emailParty = party.email;
  let emailsToAdd = [emailParty];

  let newFields = {
    [`sharingWithRole.${partyRoleCamel}`]: party.email,
    mlsNumbers: transaction.mlsNumbers || [],
  };

  if (
    partyIsAgent(party.role) &&
    party.hasTransactionCoordinator &&
    party.transactionCoordinator?.email
  ) {
    newFields[`sharingWithRole.otherTransactionCoordinator`] =
      party.transactionCoordinator.email;
    emailsToAdd.push(party.transactionCoordinator.email);
  }
  if (partyIsAgent(party.role) && party.hasCoAgent && party.coAgent?.email) {
    newFields[`sharingWithRole.otherCoAgent`] = party.coAgent.email;
    emailsToAdd.push(party.coAgent.email);
  }
  // Add assistant email if applicable
  if (party.hasAssistant && party.assistant?.email) {
    newFields[`sharingWithRole.${partyRoleCamel}Assistant`] = party.assistant.email;
    emailsToAdd.push(party.assistant.email);
  }
  if (partyIsAgent(party.role) && sharedLinkedDocId) {
    try {
      const docLinkedRef = doc(db, "documents", sharedLinkedDocId);
      if (docLinkedRef) {
        updateDoc(docLinkedRef, 
          {
            sharedStatus: true,
            sharedStatusAt: serverTimestamp(),
          }
        )
      }
    }
    catch (error) {
      console.log("error updating linked doc status");
    }
  
  }

  // If this is an agent being re-shared and we want to restore client access
  if (partyIsAgent(party.role) && allParties && restoreAgentClients) {
    const agentClients = getAgentClientParties(party, allParties, transaction);

    // Get the document to check if clients were previously shared or sent for signing
    const docSnapshot = await getDoc(docRef);
    if (docSnapshot.exists()) {
      const docData = docSnapshot.data();

      agentClients.forEach(client => {
        if (client.email) {
          const clientRoleCamel = _.camelCase(client.role);

          // Check if client was previously shared or sent for signing
          const wasPreviouslyShared = docData.sharingWithRole?.[clientRoleCamel] === client.email;
          const wasSentForSigning = docData.signingDetails?.[clientRoleCamel]?.email === client.email;

          console.log(`Client ${client.email} (${client.role}): previously shared=${wasPreviouslyShared}, sent for signing=${wasSentForSigning}`);

          if (wasPreviouslyShared || wasSentForSigning) {
            newFields[`sharingWithRole.${clientRoleCamel}`] = client.email;
            emailsToAdd.push(client.email);
            console.log(`Restoring access for client: ${client.email}`);
          }
        }
      });
    }
  }

  // Add all emails to sharingWith array
  newFields["sharingWith"] = arrayUnion(...emailsToAdd);
 
  return updateDoc(docRef, newFields);
}

export async function updateDocRemoveSharingInDb(docId, party, transaction, allParties = null, sharedLinkedDocId = null) {

  if (!docId || !party?.email) {
    console.log("no id or email", docId, party?.email);
    return;
  }
  let partyRoleCamel = _.camelCase(party.role);
  const docRef = doc(db, "documents", docId);

  // Fetch the document to check its sharingWithRole
  const docSnapshot = await getDoc(docRef);
  const documentData = docSnapshot.exists() ? docSnapshot.data() : null;
  const docSharingWithRole = documentData?.sharingWithRole || {};

  let emailsToRemove = [];
  // If this is a secondary/tertiary client who was sharing email with primary, do not remove the primary's email from sharingWith array
  if (party.sharesEmailWithPrimary) {
    const primaryRole = party.role.startsWith("Buyer") ? "buyer" : "seller";
    const docSharingWithRolePrimary = docSharingWithRole[primaryRole] || "";
    if (docSharingWithRolePrimary?.length > 0 && docSharingWithRolePrimary !== party.email) {  // shouldnt happen but check
      emailsToRemove.push(docSharingWithRolePrimary);
    }
    if (!docSharingWithRolePrimary) {
      console.log("REMOVING EMAIL from sharingWith because this is sec/terti and primary not shared", party.email);
      emailsToRemove.push(party.email);
    }
  }
  else {
    // Check if this is the primary client and if there are secondary/tertiary clients sharing the same email
    // If so, do not remove the email from sharingWith because those clients still need access
    let sharedClientStillNeedsAccess = false;

    if (party.role === "Buyer" || party.role === "Seller") {
      // This is a primary client - check if any secondary/tertiary clients share their email
      // AND if the document is still shared with those clients
      if (party.sharedEmailClients && party.sharedEmailClients.length > 0) {
        // Check document.sharingWithRole for each shared client role
        for (const sharedClient of party.sharedEmailClients) {
          const sharedClientRoleCamel = _.camelCase(sharedClient.role);
          const sharedClientEmailInDoc = docSharingWithRole[sharedClientRoleCamel] || "";
          if (sharedClientEmailInDoc && sharedClientEmailInDoc === party.email) {
            // This shared client still has the same email as primary and document is still shared with them
            sharedClientStillNeedsAccess = true;
            console.log("Primary client has sharedEmailClient who still needs access to this doc:", sharedClient.role, sharedClientEmailInDoc);
            break;
          }
        }
      }
    }

    if (!sharedClientStillNeedsAccess) {
      console.log("REMOVING EMAIL from sharingWith because this is either primary with no shared clients needing access or sec not sharing email", party.email);
      emailsToRemove.push(party.email);
    }
  }

  let newFields = {
    [`sharingWithRole.${partyRoleCamel}`]: "",
    mlsNumbers: transaction.mlsNumbers || [],
  };

  if (
    partyIsAgent(party.role) &&
    party.hasTransactionCoordinator &&
    party.transactionCoordinator?.email
  ) {
    newFields[`sharingWithRole.otherTransactionCoordinator`] = "";
    emailsToRemove.push(party.transactionCoordinator.email);
  }
  if (partyIsAgent(party.role) && party.hasCoAgent && party.coAgent?.email) {
    newFields[`sharingWithRole.otherCoAgent`] = "";
    emailsToRemove.push(party.coAgent.email);
  }
  // Handle Assistant removal
  if (party.hasAssistant && party.assistant?.email) {
    newFields[`sharingWithRole.${partyRoleCamel}Assistant`] = "";
    emailsToRemove.push(party.assistant.email);
  }

  // If this is an agent being unshared, also remove sharing with their clients
  if (partyIsAgent(party.role) && !party.isUser && allParties) {
    const agentClients = getAgentClientParties(party, allParties, transaction);

    agentClients.forEach(client => {
      if (client.email) {
        const clientRoleCamel = _.camelCase(client.role);
        newFields[`sharingWithRole.${clientRoleCamel}`] = "";
        emailsToRemove.push(client.email);
      }
    });
  }
  if (sharedLinkedDocId && party.isUser ) {
    try {
      newFields['sharedLinkedDocId'] = null;    // no longer linked as next time share it is a new doc
      const docLinkedRef = doc(db, "documents", sharedLinkedDocId);
      if (docLinkedRef) {
        updateDoc(docLinkedRef, 
          {
            sharedStatus: false,
            sharedStatusAt: serverTimestamp(),
          }
        )
      }
    }
    catch (error) {
      console.log("error updating linked doc status");
    }
  }
  

  // Remove all emails from sharingWith array
  newFields["sharingWith"] = arrayRemove(...emailsToRemove);
  return updateDoc(docRef, newFields);
}

export async function updateDocSentForSigningInDb(
  document,
  note,
  finalSignerList,
  transaction,
  additionalFields = {}
) {
  const batch = writeBatch(db);

  // Update existing party records with client email addresses (when sent by agent)
  if (additionalFields.sentForSigningByAgent) {
   
    try {
      for (const signer of finalSignerList) {

        // Find existing party record by role and transaction ID
        const existingPartyQuery = query(
          collection(db, "parties"),
          where("transactionId", "==", transaction.id),
          where("role", "==", signer.role)
        );

        const existingPartySnapshot = await getDocs(existingPartyQuery);

        // If party record exists, update it with the email and name information
        if (!existingPartySnapshot.empty) {
          const existingParty = existingPartySnapshot.docs[0];
          const partyData = existingParty.data();

          // Update the party with client information
          const updatedPartyData = {
            firstName: signer.firstName || partyData.firstName || "",
            lastName: signer.lastName || partyData.lastName || "",
            email: signer.email,
            updatedAt: serverTimestamp(),
          };

          batch.update(doc(db, "parties", existingParty.id), updatedPartyData);
        } else {
          console.log(`No existing party found for role ${signer.role}`);
        }
      }
    } catch (error) {
      console.error("Error updating party records:", error);
      // Continue with the rest of the function even if party updates fail
    }
  }

  let annots = _.cloneDeep(document.annotsInProgress);
  let annotsToSign = document.annotsToSign
    ? _.cloneDeep(document.annotsToSign)
    : {};
  let annotsByAgent = document.annotsByAgent
    ? _.cloneDeep(document.annotsByAgent)
    : {};
  const annotsInProgressForCopy = _.cloneDeep(document.annotsInProgress);
  const formFieldValuesForCopy = document.signingSentOutAt
    ? _.cloneDeep(document.formFieldValuesForCopy)
    : _.cloneDeep(document.formFieldValues);
  if (annots?.length > 0) {
    annots.forEach((annot) => {
      if (annot.agentsField && document.pdfBurnVersion) {
        annotsByAgent[annot.uniqueId] = annot;
      }
      if (!annot.agentsField) {
        annot.signerEmail = "";
        let signerFromList = finalSignerList.filter(
          (signer) => signer.role === annot.signerRole
        )[0];
        // Signer was unchecked from sending so don't create annot
        if (!signerFromList) {
          return;
        }
        if (signerFromList && signerFromList.email) {
          annot.signerEmail = signerFromList.email;
        }
        if (document.pdfBurnVersion) {
          annotsToSign[annot.uniqueId] = annot;
        } else {
          // Initial docId may be incorrect if auto added annot from CO forms
          const annotId = doc(
            collection(db, "documents", document.id, "annots")
          ).id;
          annot.id = annotId;
          annot.docId = document.id;
          annot.transactionId = transaction.id;
          annot.userId = transaction.userId;
          annot.active = true;
          annot.createdAt = serverTimestamp();
          annot.updatedAt = serverTimestamp();

          batch.set(
            doc(db, "documents", document.id, "annots", annotId),
            annot
          );
        }
      }
    });
  }

  let signingDetails = _.cloneDeep(document.signingDetails) || {};
  let signerEmails = [];
  let sharingWithRole = _.cloneDeep(document.sharingWithRole) || {};
  const finalSignerListCopy = _.cloneDeep(finalSignerList);
  if (finalSignerListCopy) {
    finalSignerListCopy.forEach((signer) => {
      const signerRoleCamel = _.camelCase(signer.role);
      signingDetails[signerRoleCamel] = signer;
      signingDetails[signerRoleCamel].sentOut = true;
      signingDetails[signerRoleCamel].sentOutAt = new Date();
      signingDetails[signerRoleCamel].signed = false;
      signingDetails[signerRoleCamel].signedAt = null;
      signerEmails = [...signerEmails, signer.email];
      sharingWithRole[signerRoleCamel] = signer.email;
    });

    // If this was sent by an agent, also create a signing detail entry for the agent
    // so that the TransActioner agent's Documents page shows the correct button state
    if (additionalFields.sentForSigningByAgent && additionalFields.sentByAgentId) {
      // Find the agent party from the transaction
      const agentParty = transaction.parties?.find(party => party.id === additionalFields.sentByAgentId);
      if (agentParty) {
        const agentRoleCamel = _.camelCase(agentParty.role);
        signingDetails[agentRoleCamel] = {
          role: agentParty.role,
          firstName: agentParty.firstName,
          lastName: agentParty.lastName,
          email: agentParty.email,
          sentOut: true,
          sentOutAt: new Date(),
          signed: false,
          signedAt: null,
          sentByAgent: true,
          agentId: additionalFields.sentByAgentId
        };
      }
    }
  }

  let formFieldValues = document.formFieldValues || {};
  let hasFormFieldValues = true;
  if (!document.pdfBurnVersion) {
    formFieldValues = {};
    hasFormFieldValues = false;
  }
  const documentFields = {
    note: note || "",
    id: document.id,
    annotsInProgress: [],
    annotsInProgressForCopy: annotsInProgressForCopy,
    annotsToSign: annotsToSign || {},
    annotsByAgent: annotsByAgent || {},
    hasFormFieldValues: hasFormFieldValues,
    formFieldValues: formFieldValues,
    hasFormFieldValuesForCopy: false,
    formFieldValuesForCopy: formFieldValuesForCopy || {},
    signingDetails: signingDetails,
    signingDetailsHistory: [signingDetails],
    signingRequestedFor: signerEmails,
    signingVerificationEnabled: true,
    sharingWith: arrayUnion(...signerEmails),
    sharingWithRole: sharingWithRole,
    status: "Awaiting Signature",
    subStatus: "Awaiting Signatures",
    signingSentOutAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  const docRef = doc(db, "documents", document.id);
  batch.update(docRef, {
    ...documentFields,
    ...additionalFields,
    updatedAt: serverTimestamp(),
  });
  const transactionFields = {
    sharingWith: arrayUnion(...signerEmails),
    sharingWithRole: sharingWithRole,
    updatedAt: serverTimestamp(),
  };
  const transactionRef = doc(db, "transactions", transaction.id);
  batch.update(transactionRef, {
    ...transactionFields,
  });
  return await batch.commit();
}

export async function updateDocSigningInDb(doc, signer, transaction) {
  let signingDetailsCopy = _.cloneDeep(doc.signingDetails);
  const signerRoleCamel = _.camelCase(signer.role);
  signingDetailsCopy[signerRoleCamel].signed = true;
  signingDetailsCopy[signerRoleCamel].signedAt = new Date();

  // Check if there are other parties sharing the same email who still need to sign
  // Only remove the email from signingRequestedFor if ALL parties with this email have signed
  const partiesSharingEmail = Object.values(signingDetailsCopy).filter(
    (detail) => detail.email === signer.email && detail.sentOut === true
  );
  const allPartiesSharingEmailSigned = partiesSharingEmail.every(
    (detail) => detail.signed === true
  );

  let signingRequestedFor = doc.signingRequestedFor;
  if (allPartiesSharingEmailSigned) {
    // All parties sharing this email have signed, so remove the email
    signingRequestedFor = doc.signingRequestedFor.filter(
      (email) => email !== signer.email
    );
  }

  // Check if this signer was sent by an agent, and if all clients sent by that agent have now signed
  const signerDetail = signingDetailsCopy[signerRoleCamel];
  if (signerDetail?.sentByAgent && signerDetail?.agentId) {
    // Find all signing details that were sent by the same agent
    const clientsSentByAgent = Object.values(signingDetailsCopy).filter(detail =>
      detail.sentByAgent && detail.agentId === signerDetail.agentId && !detail.role?.includes("Agent")
    );

    // Check if all clients sent by this agent have now signed
    const allClientsSigned = clientsSentByAgent.every(detail => detail.signed);

    if (allClientsSigned) {
      // Find the agent's signing detail and mark them as signed
      const agentSigningDetail = Object.entries(signingDetailsCopy).find(([, detail]) =>
        detail.sentByAgent && detail.agentId === signerDetail.agentId && detail.role?.includes("Agent")
      );

      if (agentSigningDetail) {
        const [agentRoleKey] = agentSigningDetail;
        signingDetailsCopy[agentRoleKey].signed = true;
        signingDetailsCopy[agentRoleKey].signedAt = new Date();
      }
    }
  }
  let documentFields = {};
  if (doc.annotsToSign) {
    Object.entries(doc.annotsToSign).forEach(([key, value]) => {
      if (
        value.signerRole === signer.role &&
        (value.type === "date" ||
          value.type === "checkbox" ||
          value.type === "text") &&
        value.signed !== true
      ) {
        documentFields[`annotsToSign.${value.uniqueId}.signed`] = true;
        documentFields[`annotsToSign.${value.uniqueId}.signedAt`] = new Date();
      }
    });
  }
  documentFields["signingDetails"] = signingDetailsCopy;
  documentFields["signingRequestedFor"] = signingRequestedFor;
  const signingComplete = docSigningComplete(signingDetailsCopy);

  if (signingComplete) {
    documentFields["status"] = "Complete";
    documentFields["statusPrevious"] = "Complete";
    documentFields["subStatus"] = "Signing Complete";
    documentFields["signingDetailsHistory"] = [
      ...doc.signingDetailsHistory,
      signingDetailsCopy,
    ];
  }

  const updateResult = await updateDocInDb(doc.id, documentFields);

  if (signingComplete && transaction) {
    // Fire-and-forget: don't let a suggestion failure block the signing flow.
    suggestListingContractDateFromSignedDoc(
      { ...doc, ...documentFields },
      transaction
    );
  }

  return updateResult;
}

export async function markNotificationsDeletedForDocInDb(docId) {
  const notificationsRef = collection(db, "notifications");
  const notificationsQuery = query(notificationsRef, where("docId", "==", docId));
  const snapshot = await getDocs(notificationsQuery);

  if (snapshot.empty) return;

  const batch = writeBatch(db);
  snapshot.forEach((notification) => {
    batch.update(notification.ref, {
      status: "deleted",
      deletedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  });
  return batch.commit();
}

export async function updateNotificationsDocNameForDocInDb(docId, docName) {
  const notificationsRef = collection(db, "notifications");
  const notificationsQuery = query(notificationsRef, where("docId", "==", docId));
  const snapshot = await getDocs(notificationsQuery);

  if (snapshot.empty) return;

  const batch = writeBatch(db);
  snapshot.forEach((notification) => {
    batch.update(notification.ref, {
      docName,
      updatedAt: serverTimestamp(),
    });
  });
  return batch.commit();
}

export async function deleteDocInDb(docId) {
  await markNotificationsDeletedForDocInDb(docId);
  const docRef = doc(db, "documents", docId);
  return deleteDoc(docRef);
}

///////////////////////////////////////////////////////////////
//////////////////////  FORMS  ////////////////////////////
///////////////////////////////////////////////////////////////

export function fetchStateFormsFromDb(currentUserProfile) {
  const agentState = currentUserProfile?.state
    ? currentUserProfile.state
    : "Colorado";
  console.log(agentState);

  const formsRef = collection(db, `forms${agentState}`);
  return query(formsRef);
}

export async function getFormFieldConversionsFromDb(formId, transaction) {
  const agentState = transaction?.agentProfile?.state
    ? transaction?.agentProfile?.state
    : "Colorado";
  const fieldConversionsRef = collection(
    db,
    `forms${agentState}`,
    formId,
    "fieldConversions"
  );
  const snapshot = await getDocs(fieldConversionsRef);
  return snapshot.docs.map((doc) => doc.data());
}

export async function getFormTasksFromDb(formId, transaction) {
  const agentState = transaction?.agentProfile?.state
    ? transaction?.agentProfile?.state
    : "Colorado";
  const formTasksRef = collection(
    db,
    `forms${agentState}`,
    formId,
    "tasksToExtract"
  );
  const snapshot = await getDocs(formTasksRef);
  return snapshot.docs.map((doc) => doc.data());
}

// documentType/documentTypeSubtype values (set on the form template in the admin
// form editor, and copied onto every document created from it) that identify a
// form as an agency/listing agreement.
const AGENCY_AGREEMENT_DOCUMENT_TYPE = "Agency";
const AGENCY_AGREEMENT_SUBTYPES = ["Listing Agreement", "Buyer Agency Agreement"];

function docIsAgencyAgreement(doc) {
  return (
    doc?.documentType === AGENCY_AGREEMENT_DOCUMENT_TYPE &&
    AGENCY_AGREEMENT_SUBTYPES.includes(doc?.documentTypeSubtype)
  );
}

function coerceFormFieldToDate(rawValue) {
  if (!rawValue) return null;
  if (rawValue?.toDate) return rawValue.toDate();
  if (rawValue instanceof Date) return rawValue;
  return parseDateSafe(rawValue);
}

// When a signed agency agreement (Listing Agreement / Buyer Agency Agreement) becomes
// fully executed, look up the "Listing Contract Date" field mapped for this specific
// form (via the same tasksToExtract admin mapping used to extract deadlines - see
// getFormTasksFromDb) and, if the signed form's value differs from what's already on
// the transaction, suggest updating transaction.listingContractDateTime.
export async function suggestListingContractDateFromSignedDoc(doc, transaction) {
  try {
    if (!doc?.formId || !transaction?.id || !docIsAgencyAgreement(doc)) return;

    const tasks = await getFormTasksFromDb(doc.formId, transaction);
    const listingContractDateTask = tasks.find(
      (task) => task.title === "Listing Contract Date"
    );
    if (!listingContractDateTask?.pdfFieldName) return;

    const suggestedDate = coerceFormFieldToDate(
      doc.formFieldValues?.[listingContractDateTask.pdfFieldName]
    );
    if (!suggestedDate) return;

    const existingDate = coerceFormFieldToDate(transaction.listingContractDateTime);
    if (existingDate && existingDate.toDateString() === suggestedDate.toDateString()) {
      return; // Already matches, nothing to suggest
    }

    const recipientIds = await buildNotificationRecipientIds(transaction);
    if (recipientIds.length === 0) return;

    const suggestedFieldLabel =
      transaction.agentRepresents === "Buyer"
        ? "Buyer Listing Contract Date"
        : transaction.agentRepresents === "Seller"
        ? "Seller Listing Contract Date"
        : "Agency Listing Contract Date";

    await addNotificationToDb({
      transactionId: transaction.id,
      transactionAddress: transaction.address?.street || transaction.title || "",
      docId: doc.id,
      docName: doc.name || doc.title || "",
      type: "listingContractDateSuggestion",
      status: "unresolved",
      recipientIds,
      suggestedField: "listingContractDateTime",
      suggestedFieldLabel,
      suggestedValue: suggestedDate,
      message: `${doc.name || "This agency agreement"} suggests a ${suggestedFieldLabel.toLowerCase()} of ${suggestedDate.toLocaleDateString()}.`,
      tcId: transaction.tcId || "",
      agentAsTcId: transaction.agentAsTcId || "",
      managerId: transaction.managerId || "",
    });
  } catch (error) {
    console.error("Error creating listing contract date suggestion:", error);
  }
}

export async function addUploadedFormToDb(transaction, docPath, docId, form) {
  let suggestedAnnots = [];
  let signingDetails = {};
  if (form.annotsInProgressSuggested) {
    suggestedAnnots = createSuggestedAnnots(transaction, form);
    signingDetails = createInitialSigningDetails(transaction, form);
  }
  const docRef = doc(db, "documents", docId);
  return setDoc(docRef, {
    ...form,
    mlsNumber: transaction.mlsNumber || "",
    annotsInProgress: suggestedAnnots,
    annotsInProgressSuggestedAdded: false,
    annotsToSign: {},
    annotsByAgent: {},
    signingDetails: signingDetails,
    hasFormFieldValues: true,
    hasFormFieldValuesForCopy: false,
    userId: transaction.userId,
    tcId: transaction.tcId || "",
    agentAsTcId: transaction.agentAsTcId || "",
    managerId: transaction.managerId || "",
    coAgentId: transaction.coAgentId || "",
    userEmail: transaction.agentEmail || transaction.agentProfile?.email || "",
    userFullname:
      transaction.agentName ||
      transaction.agentProfile?.firstName +
        " " +
        transaction.agentProfile?.lastName ||
      "",
    userFirstName:
      transaction.agentFirstName || transaction.agentProfile?.firstName || "",
    userLastName:
      transaction.agentLastName || transaction.agentProfile?.lastName || "",
    docRef: docPath,
    id: docId,
    signed: false,
    formId: form.id,
    transactionId: transaction.id,
    transactionTitle: transaction.title,
    timeZone: transaction.timeZone || "America/Denver",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    status: "In Progress",
    subStatus: "Filling Out Form",
    name: form.name ? form.name : form.title,
    filetype: "pdf",
    filetypeMIME: "application/pdf",
    pdfBurnVersion: 2,
    hasSigningVerification: true,
  });
}

///////////////////////////////////////////////////////////////
/////////////////////////  EMAILS  ////////////////////////////
/////////////////////////

export function fetchEmailsFromDb(transactionId) {
  const emailsRef = collection(db, "emailHistory");
  return query(emailsRef, //where("userId", "==", auth.currentUser.uid),
    where("transactionId", "==", transactionId));
}

// Emails a party-portal agent sent to their own clients (see restrictedToPartyId
// in logDocSharingEmailHistory). Scoped to that one party record so the main
// transaction's Email History page never surfaces these to the other side.
export function fetchPartyEmailHistoryFromDb(transactionId, partyId) {
  const emailsRef = collection(db, "emailHistory");
  return query(
    emailsRef,
    where("transactionId", "==", transactionId),
    where("restrictedToPartyId", "==", partyId)
  );
}

export function addEmailToDb(values, emailFields) {
  const emailId = doc(collection(db, "emailHistory")).id;
  return setDoc(doc(db, "emailHistory", emailId), {
    ...values,
    ...emailFields,    id: emailId,
    userId: auth.currentUser.uid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function sendComplianceNotificationEmail(mailOptions) {
  const mailRef = collection(db, "mail");
  if (process.env.REACT_APP_ENVIRONMENT === "development") {
    console.log("COMPLIANCE EMAIL THAT WOULD BE SENT: ", mailOptions);
  } else {
    await addDoc(mailRef, {
      ...mailOptions,
      createdAt: serverTimestamp(),
    });
  }
}

export async function sendNotificationEmail(mailOptions) {
  const mailRef = collection(db, "mail");
  if (process.env.REACT_APP_ENVIRONMENT === "development") {
    console.log("NOTIFICATION EMAIL THAT WOULD BE SENT: ", mailOptions);
  } else {
    await addDoc(mailRef, {
      ...mailOptions,
      createdAt: serverTimestamp(),
    });
  }
}

///////////////////////////////////////////////////////////////
///////////////////  FORM TEMPLATES  //////////////////////////
///////////////////////////////////////////////////////////////

// Fetch manager's form templates for agents
export function fetchManagerFormTemplatesFromDb(managerId) {
  const formTemplatesRef = collection(db, "formTemplates");
  return query(
    formTemplatesRef,
    where("userId", "==", managerId),
    where("dontShowFormInAddDocumentModal", "==", false), 
    orderBy("title", "desc")
  );
}

export function fetchFormTemplatesFromDb() {
  const formTemplatesRef = collection(db, "formTemplates");
  return query(
    formTemplatesRef,
    where("userId", "==", auth.currentUser.uid), // templates not shared with asst/tcs
    // cannot filter here or will disappear from their edit templates list which is weird
    // where("dontShowFormInAddDocumentModal", "!=", true),
    // orderBy("dontShowFormInAddDocumentModal", "asc"),
    orderBy("title", "desc")
  );
}
export function copyFormTemplateToDb(formTemplate) {
  const templateId = doc(collection(db, "formTemplates")).id;
  return setDoc(doc(db, "formTemplates", templateId), {
    ...formTemplate,
    id: templateId,
    userId: auth.currentUser.uid, // templates not shared with asst/tcs
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export function addFormTemplateToDb(formTemplates, currentUserProfile) {
  const batch = writeBatch(db);
  formTemplates.forEach((template) => {
    const templateId = doc(collection(db, "formTemplates")).id;
    const isShowAgentNotSetOnForm =
      !template.showSellerAgent && !template.showBuyerAgent;
    batch.set(doc(db, "formTemplates", templateId), {
      userId: currentUserProfile.userId,
      hasFormFieldValues: false,
      title: template.title,
      name: template.title,
      id: templateId,
      showSellerAgent:
        isShowAgentNotSetOnForm || (template.showSellerAgent ? true : false),
      showBuyerAgent:
        isShowAgentNotSetOnForm || (template.showBuyerAgent ? true : false),
      dontShowFormInAddDocumentModal: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  });
  return batch.commit();
}

export function updateFormTemplateInDb(formTemplateId, formTemplateFields) {
  const docRef = doc(db, "formTemplates", formTemplateId);
  return updateDoc(docRef, {
    ...formTemplateFields,
    updatedAt: serverTimestamp(),
  });
}

export function deleteFormTemplateInDb(formTemplateId) {
  const docRef = doc(db, "formTemplates", formTemplateId);
  return deleteDoc(docRef);
}

///////////////////////////////////////////////////////////////
//////////////////  DEADLINE TEMPLATES  ///////////////////////
///////////////////////////////////////////////////////////////

// Fetch manager's deadline templates for agents
export function fetchManagerDeadlineTemplatesFromDb(managerId) {
  const deadlineTemplatesRef = collection(db, "deadlineTemplates");
  return query(
    deadlineTemplatesRef,
    where("userId", "==", managerId)
  );
}

export function fetchDeadlineTemplatesFromDb() {
  const deadlineTemplatesRef = collection(db, "deadlineTemplates");
  return query(
    deadlineTemplatesRef,
    where("userId", "==", auth.currentUser.uid)
  );
}
export function copyDeadlineTemplateToDb(deadlineTemplate) {
  const templateId = doc(collection(db, "deadlineTemplates")).id;
  return setDoc(doc(db, "deadlineTemplates", templateId), {
    ...deadlineTemplate,
    id: templateId,
    userId: auth.currentUser.uid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export function addDeadlineTemplateToDb(deadlineTemplate) {
  const templateId = doc(collection(db, "deadlineTemplates")).id;
  return setDoc(doc(db, "deadlineTemplates", templateId), {
    ...deadlineTemplate,
    id: templateId,
    title: deadlineTemplate.name,
    name: deadlineTemplate.name,
    userId: auth.currentUser.uid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export function updateDeadlineTemplateInDb(
  deadlineTemplateId,
  deadlineTemplateFields
) {
  const docRef = doc(db, "deadlineTemplates", deadlineTemplateId);
  return updateDoc(docRef, {
    ...deadlineTemplateFields,
    updatedAt: serverTimestamp(),
  });
}

export function deleteDeadlineTemplateInDb(deadlineTemplateId) {
  const docRef = doc(db, "deadlineTemplates", deadlineTemplateId);
  return deleteDoc(docRef);
}

///////////////////////////////////////////////////////////////
/////////////////////  CE CERTS  ///////////////////////
///////////////////////////////////////////////////////////////

export function fetchCeCertsFromDb() {
  const ceCertsRef = collection(db, "ceCerts");
  return query(ceCertsRef, where("userId", "==", auth.currentUser.uid), orderBy("dateCertificate", "desc"));
}

export function addCeCertToDb(ceCert) {
  const ceCertId = doc(collection(db, "ceCerts")).id;
  return setDoc(doc(db, "ceCerts", ceCertId), {
    ...ceCert,
    id: ceCertId,
    userId: auth.currentUser.uid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export function updateCeCertInDb(
  ceCertId,
  ceCertFields
) {
  const docRef = doc(db, "ceCerts", ceCertId);
  return updateDoc(docRef, {
    ...ceCertFields,
    updatedAt: serverTimestamp(),
  });
}

export function deleteCeCertInDb(ceCertId) {
  const docRef = doc(db, "ceCerts", ceCertId);
  return deleteDoc(docRef);
}

///////////////////////////////////////////////////////////////
/////////////////////  EMAIL TEMPLATES  ///////////////////////
///////////////////////////////////////////////////////////////

// Fetch manager's email templates for agents
export function fetchManagerEmailTemplatesFromDb(managerId) {
  const emailTemplatesRef = collection(db, "emailTemplatesUsers");
  return query(emailTemplatesRef, where("userId", "==", managerId));
}

export function fetchEmailTemplatesFromDb() {
  const emailTemplatesRef = collection(db, "emailTemplatesUsers");
  return query(emailTemplatesRef, where("userId", "==", auth.currentUser.uid));
}
export function copyEmailTemplateToDb(emailTemplate) {
  const templateId = doc(collection(db, "emailTemplatesUsers")).id;
  return setDoc(doc(db, "emailTemplatesUsers", templateId), {
    ...emailTemplate,
    id: templateId,
    userId: auth.currentUser.uid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export function addEmailTemplateToDb(
  emailTemplate,
  toRoles,
  ccRoles,
  bccRoles
) {
  const templateId = doc(collection(db, "emailTemplatesUsers")).id;
  return setDoc(doc(db, "emailTemplatesUsers", templateId), {
    ...emailTemplate,
    to: toRoles,
    cc: ccRoles,
    bcc: bccRoles,
    id: templateId,
    title: emailTemplate.name,
    name: emailTemplate.name,
    userId: auth.currentUser.uid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export function renameEmailTemplateInDb(emailTemplateId, emailTemplateFields) {
  const docRef = doc(db, "emailTemplatesUsers", emailTemplateId);
  return updateDoc(docRef, {
    ...emailTemplateFields,
    updatedAt: serverTimestamp(),
  });
}

export function updateEmailTemplateInDb(
  emailTemplateId,
  emailTemplateFields,
  toRoles,
  ccRoles,
  bccRoles
) {
  const docRef = doc(db, "emailTemplatesUsers", emailTemplateId);
  return updateDoc(docRef, {
    ...emailTemplateFields,
    to: toRoles,
    cc: ccRoles,
    bcc: bccRoles,
    updatedAt: serverTimestamp(),
  });
}

export function deleteEmailTemplateInDb(emailTemplateId) {
  const docRef = doc(db, "emailTemplatesUsers", emailTemplateId);
  return deleteDoc(docRef);
}

///////////////////////////////////////////////////////////////
/////////////////////  TASK TEMPLATES  ////////////////////////
///////////////////////////////////////////////////////////////

// Fetch manager's task templates for agents
export function fetchManagerTaskTemplatesFromDb(managerId) {
  const taskTemplatesRef = collection(db, "taskTemplates");
  return query(taskTemplatesRef, where("userId", "==", managerId));
}

export function fetchTaskTemplatesFromDb() {
  const taskTemplatesRef = collection(db, "taskTemplates");
  return query(taskTemplatesRef, where("userId", "==", auth.currentUser.uid));
}

export function copyTaskTemplateToDb(taskTemplate) {
  const templateId = doc(collection(db, "taskTemplates")).id;
  return setDoc(doc(db, "taskTemplates", templateId), {
    ...taskTemplate,
    id: templateId,
    userId: auth.currentUser.uid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export function addTaskTemplateToDb(taskTemplate) {
  const templateId = doc(collection(db, "taskTemplates")).id;
  return setDoc(doc(db, "taskTemplates", templateId), {
    ...taskTemplate,
    id: templateId,
    title: taskTemplate.title,
    name: taskTemplate.title,
    autoAddToBuyerTransactions: taskTemplate.autoAddToBuyerTransactions || false,
    autoAddToSellerTransactions: taskTemplate.autoAddToSellerTransactions || false,
    userId: auth.currentUser.uid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export function addTaskTemplateItemToDb(item) {
  const templateId = doc(collection(db, "taskTemplates")).id;
  return setDoc(doc(db, "taskTemplates", templateId), {
    ...item,
    id: templateId,
    title: item.title,
    name: item.title,
    userId: auth.currentUser.uid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export function updateTaskTemplateInDb(taskTemplateFields) {
  const docRef = doc(db, "taskTemplates", taskTemplateFields.id);
  return updateDoc(docRef, {
    ...taskTemplateFields,
    updatedAt: serverTimestamp(),
  });
}

export function deleteTaskTemplateInDb(taskTemplateId) {
  const docRef = doc(db, "taskTemplates", taskTemplateId);
  return deleteDoc(docRef);
}

///////////////////////////////////////////////////////////////
///////////////////////  CLAUSES  /////////////////////////////
///////////////////////////////////////////////////////////////

export function fetchClausesFromDb(currentUserProfile) {
  let queryRef = collection(db, "clauses");
  const brokerage = currentUserProfile?.brokerageForms
    ? currentUserProfile.brokerageForms
    : "NA";

  // Build the query conditions
  const queryConditions = [
      where("userId", "==", auth.currentUser.uid), // User's own clauses
    ];

  // If user is an agent with a managerId, also include manager's user clauses
  if (currentUserProfile?.managerId 
    && (currentUserProfile?.role === "agent" 
        || currentUserProfile?.role === "assistant" 
        || currentUserProfile?.role === "managerassistant")
     ) {
    queryConditions.push(
        where("userId", "==", currentUserProfile.managerId),
    );
  }

  if (brokerage && brokerage !== "NA") {
    queryConditions.push(
        where("brokerage", "==", brokerage)
    );
  }

  if (currentUserProfile?.additionalFormsAccess) {
    currentUserProfile.additionalFormsAccess.forEach((formAccess) => {
      if (formAccess?.startsWith("LEGAL_")) {
        queryConditions.push(
        where("additionalFormsGroupId", "==", formAccess)
        );
      }
    });
  }

  return query(
    queryRef,
    or(...queryConditions),
    orderBy("title", "asc")
  );
}

export function addClauseToDb(clause) {
  const clauseId = doc(collection(db, "clauses")).id;
  return setDoc(doc(db, "clauses", clauseId), {
    ...clause,
    id: clauseId,
    title: clause.title,
    name: clause.title,
    userId: auth.currentUser.uid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export function updateClauseInDb(clauseId, clauseFields) {
  const docRef = doc(db, "clauses", clauseId);
  return updateDoc(docRef, {
    ...clauseFields,
    updatedAt: serverTimestamp(),
  });
}

export function deleteClauseInDb(clauseId) {
  const docRef = doc(db, "clauses", clauseId);
  return deleteDoc(docRef);
}

///////////////////////////////////////////////////////////////
//////////////////  BROKERAGE FORMS  /////////////////
///////////////////////////////////////////////////////////////

export function fetchBrokerageFormsFromDb(state, brokerage) {
  const formsRef = collection(db, `forms${state}`);
  return query(
    formsRef,
    where("brokerage", "==", brokerage),
    where("dontShowFormInAddDocumentModal", "==", false),
  );
}

export function updateBrokerageFormInDb(state, formId, brokerageFormFields) {
  const docRef = doc(db, `forms${state}`, formId);
  return updateDoc(docRef, {
    ...brokerageFormFields,
    updatedAt: serverTimestamp(),
  });
}

///////////////////////////////////////////////////////////////
////////////////////////  HISTORY  ////////////////////////////
///////////////////////////////////////////////////////////////

export function fetchHistoryFromDb(id) {
  const historyRef = collection(db, "history");
  return query(
    historyRef,
    where("transactionId", "==", id),
    orderBy("createdAt", "desc")
  );
}

export function addHistoryToDb(
  transactionId,
  party,
  action,
  documentName,
  toParty,
  userTransactionRole = null,
  restrictedToPartyId = null,
) {
  const historyId = doc(collection(db, "history")).id;
  return setDoc(doc(db, "history", historyId), {
    transactionId: transactionId,
    role: userTransactionRole || party?.role || "",          // currentUserProfile sent so role is wrong when agent is TC
    firstName: party?.firstName || "",
    lastName: party?.lastName || "",
    hasPoa: party?.hasPoa || false,
    poaFullName: party?.poaFullName || "",
    action: action || "",
    toRole: toParty?.role || "",
    toFirstName: toParty?.firstName || "",
    toLastName: toParty?.lastName || "",
    toEmail: toParty?.email || "",
    toHasPoa: toParty?.hasPoa || false,
    toPoaFullName: toParty?.poaFullName || "",
    document: documentName || "",
    id: historyId,
    userId: auth.currentUser.uid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    // Same idea as emailHistory's restrictedToPartyId: when a party-portal
    // agent shares with their own clients, this scopes the entry to their
    // own party-portal History timeline (fetchPartyHistoryFromDb) without
    // affecting the main transaction's History log, which still shows
    // everything via fetchHistoryFromDb.
    ...(restrictedToPartyId ? { restrictedToPartyId } : {}),
  });
}

// Documents this party (agent/coagent/TC sharing the same party portal link)
// has shared or sent for signing with their own clients — see
// restrictedToPartyId in addHistoryToDb. Scoped to that one party record, same
// pattern as fetchPartyEmailHistoryFromDb.
export function fetchPartyHistoryFromDb(transactionId, partyId) {
  const historyRef = collection(db, "history");
  return query(
    historyRef,
    where("transactionId", "==", transactionId),
    where("restrictedToPartyId", "==", partyId)
  );
}

///////////////////////////////////////////////////////////////
////////////////////////  NOTIFICATIONS  //////////////////////
///////////////////////////////////////////////////////////////

// Fetch notifications for users who have access to the transaction
// This includes: agent, co-agent, assistant, TC, manager, manager assistant
export function fetchNotificationsForUserFromDb(userId) {
  const notificationsRef = collection(db, "notifications");
  return query(
    notificationsRef,
    where("recipientIds", "array-contains", userId),
    where("status", "==", "unresolved"),
    orderBy("createdAt", "desc")
  );
}

// Fetch all notifications for a specific transaction
export function fetchNotificationsForTransactionFromDb(transactionId) {
  const notificationsRef = collection(db, "notifications");
  return query(
    notificationsRef,
    where("transactionId", "==", transactionId),
    orderBy("createdAt", "desc")
  );
}

// Helper function to build recipientIds array for notifications
export async function buildNotificationRecipientIds(transaction) {
  const recipientIds = [];

  // Add agent
  if (transaction.userId) recipientIds.push(transaction.userId);

  // Add co-agent (stored as transaction.coAgent.userId)
  //if (transaction.coAgent?.userId) recipientIds.push(transaction.coAgent.userId);

  if (transaction.coAgentId) recipientIds.push(transaction.coAgentId);

  // Add assistants (array of objects with userId)
  if (transaction.assistant && Array.isArray(transaction.assistant)) {
    transaction.assistant.forEach((asst) => {
      if (asst?.userId) recipientIds.push(asst.userId);
    });
  }

  // Add TC
  if (transaction.tcId) recipientIds.push(transaction.tcId);

  // Add agent as TC
  if (transaction.agentAsTcId) recipientIds.push(transaction.agentAsTcId);

  // Add manager
  if (transaction.managerId) recipientIds.push(transaction.managerId);

  // Fetch manager assistants from the manager's user document
  if (transaction.managerId) {
    try {
      const managerProfile = await getUserProfileFromDb(transaction.managerId);
      if (
        managerProfile?.managerAssistants &&
        Array.isArray(managerProfile.managerAssistants)
      ) {
        managerProfile.managerAssistants.forEach((asst) => {
          if (asst?.userId) recipientIds.push(asst.userId);
        });
      }
    } catch (error) {
      console.error("Error fetching manager assistants:", error);
      // Continue without manager assistants if there's an error
    }
  }

  return recipientIds;
}

export function addNotificationToDb(notificationData) {
  const notificationId = doc(collection(db, "notifications")).id;
  return setDoc(doc(db, "notifications", notificationId), {
    ...notificationData,
    id: notificationId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export function updateNotificationInDb(notificationId, updates) {
  const notificationRef = doc(db, "notifications", notificationId);
  return updateDoc(notificationRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

export async function sendPartyFormReviewedNotificationEmail(
  transaction,
  document,
  party,
  message
) {
  if (process.env.REACT_APP_ENABLE_EMAILS === "true") {
    console.log("Emails are live for this environment");

    const urlApp =
      process.env.REACT_APP_ENVIRONMENT === "development"
        ? "http://localhost:3000"
        : "https://app.transactioner.com";

    // Build recipient IDs list using the same logic as notifications
    const recipientIds = await buildNotificationRecipientIds(transaction);

    if (recipientIds.length === 0) {
      console.log("No recipients found for notification email");
      return;
    }

    // Fetch user profiles and filter by email notification preferences
    const recipients = [];

    for (const userId of recipientIds) {
      try {
        const userProfile = await getUserProfileFromDb(userId);

        if (userProfile?.email) {
          // Check if user has formReviewedNotifications enabled.
          // Falls back to the legacy top-level field for any profiles saved
          // before this preference was grouped under notificationPreferences.
          // Default to true if not set (for backwards compatibility)
          // Always include the main agent (transaction.userId)
          const formReviewedNotificationsEnabled =
            userProfile.notificationPreferences
              ?.formReviewedNotifications ??
            userProfile.formReviewedNotifications;
          if (
            userId === transaction.userId ||
            formReviewedNotificationsEnabled !== false
          ) {
            recipients.push(userProfile.email);
          }
        }
      } catch (error) {
        console.error(`Error fetching user profile for ${userId}:`, error);
        // Continue with other recipients
      }
    }

    if (recipients.length === 0) {
      console.log("No recipients with notifications enabled");
      return;
    }

    const mailRef = collection(db, "mail");
    const mailOptions = {
      to: recipients,
      from: "TransActioner <help@transactioner.com>",
      replyTo: party.email || "help@transactioner.com",
      message: {
        subject: `${party.firstName} ${party.lastName} has finished reviewing ${document.name}`,
        html: `
          <p>Hello,</p>
          <p><strong>${party.firstName} ${party.lastName}</strong> (${party.role}) has notified you that they have finished reviewing or filling out the document:</p>
          <p><strong>${document.name}</strong></p>
          ${message ? `<p><strong>Message from ${party.firstName}:</strong></p><p>${message}</p>` : ""}
          <p><strong>Transaction:</strong> ${transaction.address?.street || transaction.title || "Untitled Transaction"}</p>
          <p><a href="${urlApp}/transactions/${transaction.id}/documents">View Transaction Documents</a></p>
          <p>Best regards,<br/>TransActioner</p>
        `,
        text: `${party.firstName} ${party.lastName} (${party.role}) has finished reviewing ${document.name}. ${message ? `Message: ${message}` : ""} Transaction: ${transaction.address?.street || transaction.title}. View at: ${urlApp}/transactions/${transaction.id}/documents`,
      },
      createdAt: serverTimestamp(),
    };

    try {
      if (process.env.REACT_APP_ENVIRONMENT === "development") {
        console.log("EMAIL THAT WOULD BE SENT: ", mailOptions);
        return;
      } else {
        return addDoc(mailRef, mailOptions);
      }
    } catch (error) {
      throw error;
    }
  } else {
    console.log("No emails for this environment");
  }
}

///////////////////////////////////////////////////////////////
/////////////////////////  ANNOTS  ////////////////////////////
///////////////////////////////////////////////////////////////


export async function fetchDocAnnotsAllFromDb(docId) {
  const q = query(
    collection(db, "documents", docId, "annots"),
    where("userId", "==", auth.currentUser.uid)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => doc.data());
}

export function fetchDocAnnotsFromDb(docId, signerRole) {
  const annotsRef = collection(db, "documents", docId, "annots");
  return query(
    annotsRef,
    where("signerRole", "==", signerRole),
    where("active", "!=", false)
  );
}

export function addAnnotToDb(annot, annotId, docId) {
  const annotRef = doc(db, "documents", docId, "annots", annotId);
  return setDoc(annotRef, {
    ...annot,
    id: annotId,
    docId: docId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export function updateAnnotInDb(annotId, docId, annotFields) {
  const docRef = doc(db, "documents", docId, "annots", annotId);
  return updateDoc(docRef, {
    ...annotFields,
    updatedAt: serverTimestamp(),
  });
}

export function updateAnnotsForDocInDb(annots, docId, annotFields) {
  annots.forEach((annot) => {
    updateAnnotInDb(annot.id, docId, annotFields);
  });
  return;
}

export function updateAnnotSignedInDb(annotId, docId, annotFields) {
  const docRef = doc(db, "documents", docId, "annots", annotId);
  return updateDoc(docRef, {
    ...annotFields,
    signed: true,
    signedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function deleteDocAnnotsInDb(docId) {
  const user = auth.currentUser;
  const q = query(
    collection(db, "documents", docId, "annots"),
    where("docId", "==", docId),
    where("userId", "==", user.uid)
  );
  const snapshot = await getDocs(query(q));
  snapshot.forEach((annot) => {
    deleteAnnotInDb(docId, annot.id);
  });
  return;
}

export function deleteAnnotInDb(docId, annotId) {
  const docRef = doc(db, "documents", docId, "annots", annotId);
  return deleteDoc(docRef);
}

///////////////////////////////////////////////////////////////
//////////////////////////  USERS  ////////////////////////////
///////////////////////////////////////////////////////////////

export function getUserProfile(userId) {
  const docRef = doc(db, "users", userId);
  return docRef;
}

export async function getUserProfileFromDb(userId) {
  const partyRef = doc(db, "users", userId);
  const snapshot = await getDoc(partyRef);
  return snapshot.data();
}

export async function getUserProfileByEmailFromDb(email) {
  const q = query(collection(db, "users"), where("email", "==", email));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => doc.data());
}

export function fetchAgentsForAssistantFromDb() {
  let queryRef = query(collection(db, "users"));
  if (authClaims?.r === "a" && authClaims?.a) {
    return query(queryRef, where("userId", "in", authClaims.a));
  } else return query(queryRef, where("userId", "==", "111111"));
}

export function fetchAgentsForManagerFromDb() {
  let queryRef = query(collection(db, "users"), where("type", "==", "user"), where("role", "==", "agent"));
  if (authClaims?.r === "m") {
    return query(queryRef, where("managerId", "==", auth.currentUser.uid));
  } else if (authClaims?.r === "g" && authClaims?.m) {
    // Manager Assistant can see agents for all managers they assist
    return query(queryRef, where("managerId", "in", authClaims.m));
  }
  return query(queryRef, where("managerId", "==", "no-results"));
}

export function fetchManagerAssistantsForManagerFromDb() {
  let queryRef = query(collection(db, "users"), where("type", "==", "user"), where("role", "==", "managerassistant"));
  if (authClaims?.r === "m") {
    return query(queryRef, where("managerId", "==", auth.currentUser.uid));
  } else if (authClaims?.r === "g" && authClaims?.m) {
    // Manager Assistant can see agents for all managers they assist
    return query(queryRef, where("managerId", "in", authClaims.m));
  }
  return query(queryRef, where("managerId", "==", "no-results"));
}

export async function fetchAllManagersFromDb() {
  const queryRef = query(collection(db, "users"), where("role", "==", "manager"));
  const snapshot = await getDocs(queryRef);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
}

export function addPartyUserToDb(partyInfo) {
  const user = auth.currentUser;
  const userRef = doc(db, "users", user.uid);
  return setDoc(userRef, {
    displayName: user.displayName || "",
    email: user.email,
    firstName: partyInfo?.partyFirstName || "",
    lastName: partyInfo?.partyLastName || "",
    providerId: user.providerId,
    id: user.uid,
    userId: user.uid,
    type: "party",
    numLogins: 1,
    lastSignIn: serverTimestamp(),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export function updateLastSignIn(uid) {
  const docRef = doc(db, "users", uid);
  return updateDoc(docRef, {
    lastSignIn: serverTimestamp(),
    numLogins: increment(1),
    updatedAt: serverTimestamp(),
  });
}

export async function updateUserProfileInDb(profile) {
  const docRef = doc(db, "users", auth.currentUser.uid);
  return await updateDoc(docRef, {
    ...profile,
    updatedAt: serverTimestamp(),
  });
}

export async function updateProfileFieldsInDb(userId, profileFields) {
  const docRef = doc(db, "users", userId);
  return await updateDoc(docRef, {
    ...profileFields,
    updatedAt: serverTimestamp(),
  });
}

export async function dismissWeeklyDeadlineSuggestionForTransactionInDb(
  transactionId,
  weekKey
) {
  const docRef = doc(db, "transactions", transactionId);
  return await updateDoc(docRef, {
    dismissedWeeklyDeadlineWeeks: arrayUnion(weekKey),
    updatedAt: serverTimestamp(),
  });
}


///////////////////////////////////////////////////////////////
//////////////////////  MANAGERS  /////////////////////////////
///////////////////////////////////////////////////////////////

// export function fetchTransActiveForManagerFromDb() {
//   let queryRef = query(
//     collection(db, "transactions"),
//     where("active", "==", true),
//     where("managerId", "==", auth.currentUser.uid)
//   );
//   return query(queryRef);
// }
// NOTE - manager assistant can only support one manager at the moment
export function fetchTransActiveForManagerFromDb() {
  let queryRef = query(
    collection(db, "transactions"),
    where("active", "==", true),
    where("managerId", "==",
      (authClaims?.r === "m" ? auth.currentUser.uid :
        (authClaims?.r === "g" && authClaims?.m?.[0] ? authClaims.m[0] : auth.currentUser.uid)
      )
    )
  );
  return query(queryRef);
}

// export function fetchTransActiveForManagerFromDb() {
//   let queryRef = collection(db, "transactions");
//   if (authClaims?.r === "m") {
//     queryRef = query(queryRef, where("managerId", "==", auth.currentUser.uid));
//   } else if (authClaims?.r === "g" && authClaims?.m) {
//     // Manager Assistant can see transactions for all managers they assist
//     queryRef = query(queryRef, where("managerId", "in", authClaims.m));
//   }
//   return query(
//     queryRef,
//     where("status", "!=", "Complete"),
//     orderBy("status"),
//     orderBy("updatedAt", "desc")
//   );
// }

// export function fetchTransClosedForManagerFromDb() {
//   let queryRef = query(
//     collection(db, "transactions"),
//     where("status", "==", "Complete"),
//     where("managerId", "==", auth.currentUser.uid),
//     where("closingDateTime", ">", startOfYear(new Date()))
//   );
//   return query(queryRef);
// }

export function fetchTransClosedForManagerFromDb() {
  let queryRef = collection(db, "transactions");
  if (authClaims?.r === "m") {
    queryRef = query(queryRef, where("managerId", "==", auth.currentUser.uid));
  } else if (authClaims?.r === "g" && authClaims?.m) {
    // Manager Assistant can see transactions for all managers they assist
    queryRef = query(queryRef, where("managerId", "==", authClaims.m[0]));
  }
  return query(
    queryRef,
    where("status", "==", "Complete"),
    where("closingDateTime", ">", startOfYear(new Date()))
    // orderBy("updatedAt", "desc")
  );
}

export function fetchTransClosedForAgentFromDb() {
  // if (!auth.currentUser) {
  //   return query(collection(db, "transactions"), where("id", "==", "nonexistent"));
  // }

  let queryRef = collection(db, "transactions");

  // Start with basic filters - temporarily simplified for testing
  queryRef = query(
    queryRef,
    where("status", "==", "Complete")
  );

  // Add user-specific filters based on auth claims
  if (authClaims?.r === "a" && authClaims?.a) {
    queryRef = query(queryRef, where("userId", "in", authClaims.a));
  } else if (authClaims?.r === "t") {
    queryRef = query(queryRef, where("tcId", "==", auth.currentUser.uid));
  } else if (authClaims?.r === "g" && authClaims?.m) {
    queryRef = query(queryRef, where("managerId", "==", authClaims.m[0]));
  } else {
    // For regular agents, check all possible relationships
    queryRef = query(
      queryRef,
      or(
        where("userId", "==", auth.currentUser.uid),
        where("coAgentId", "==", auth.currentUser.uid),
        where("tcId", "==", auth.currentUser.uid),
        where("agentAsTcId", "==", auth.currentUser.uid)
      )
    );
  }
  return queryRef;
}



export function fetchTasksUpcomingForManagerFromDb(id = null) {
  let queryRef = query(collection(db, "tasks"));
  
  queryRef = query(queryRef, where("managerId", "==",
    (authClaims?.r === "m" ? auth.currentUser.uid :
      (authClaims?.r === "g" && authClaims?.m?.[0]) ? authClaims.m[0] :  auth.currentUser.uid)));
  
    return query(
      queryRef,
      where("end", ">=", startOfDay(subDays(new Date(), 1))),
      orderBy("end"),
      limit(400)
    );
  
 
}

///////////////////////////////////////////////////////////////
//////////////////  PROCESSING FORMS  /////////////////////////
///////////////////////////////////////////////////////////////

export async function getStateFormByTitleFromDb(title, currentUserProfile) {
  const agentState = currentUserProfile.state || "Colorado";
  const q = query(
    collection(db, `forms${agentState}`),
    where("title", "==", title)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))[0];
}

export async function updateStateFormInDb(
  formId,
  documentFields,
  currentUserProfile
) {
  const agentState = currentUserProfile.state || "Colorado";
  const docRef = doc(db, `forms${agentState}`, formId);
  return updateDoc(docRef, {
    ...documentFields,
  });
}

///////////////////////////////////////////////////////////////
//////////////////////////  EMAIL  ////////////////////////////
///////////////////////////////////////////////////////////////

export async function sendOfferSubmittedEmail(
  sender,
  recipient,
  transaction,
  document
) {
  if (process.env.REACT_APP_ENABLE_EMAILS === "true") {
    console.log("Emails are live for this environment");
    if (
      sender?.isTester !== true &&
      (transaction?.agentEmail || transaction.agentProfile?.email)
    ) {
      console.log("Emails are live for this user");
      const transactionAddress = transaction?.address
        ? convertAddressStreetAndUnit(transaction.address)
        : "";
      sendEmail(
        ["liz@transactioner.com"],
        ["john@transactioner.com"],
        "offerSubmittedToAdmin",
        {
          senderFirstName: sender.firstName,
          senderLastName: sender.lastName,
          senderEmail: sender.email,
          recipientFirstName: recipient.firstName,
          recipientLastName: recipient.lastName,
          recipientEmail: recipient.email,
          transactionAddress: transactionAddress,
          documentName: document.name || "",
        }
      );
    } else {
      console.log("No emails for this user");
    }
  } else {
    console.log("No emails for this environment");
  }
}

export async function sendDocSignedEmail(sender, transaction, document) {
  if (process.env.REACT_APP_ENABLE_EMAILS === "true") {
    console.log("Emails are live for this environment");
    let agentEmail = transaction.agentEmail
      ? transaction.agentEmail
      : transaction.agentProfile?.email
      ? transaction.agentProfile?.email
      : "";
    const agentIsTester = transaction.agentProfile?.isTester ? true : false;
    if (sender?.isTester !== true && agentEmail && !agentIsTester) {
      console.log("Emails are live for this user");
      const urlApp =
        process.env.REACT_APP_ENVIRONMENT === "development"
          ? "http://localhost:3000"
          : "https://app.transactioner.com";
      let assistantEmail = "";
      if (transaction.assistantEmail) {
        assistantEmail = transaction.assistantEmail;
      }
      const transactionAddress = transaction?.address
        ? convertAddressStreetAndUnit(transaction.address)
        : "";
      const agentFirstName = transaction.agentName
        ? transaction.agentName.split(" ")[0]
        : transaction.agentProfile?.firstName
        ? transaction.agentProfile.firstName
        : "";

        //console.log("sendDocSignedEmail::: Sending email to: ", agentEmail, assistantEmail);

      sendEmail([agentEmail], [assistantEmail], "signingCompleteToAgent", {
        recipientFirstName: agentFirstName,
        loginUrlUser: urlApp,
        senderFirstName: sender.firstName,
        senderLastName: sender.lastName,
        senderEmail: sender.email,
        transactionAddress: transactionAddress,
        documentName: document.name || "",
      });
      if (transaction.hasTc && transaction.transactionCoordinator?.email) {
        // console.log("sendDocSignedEmail::: Sending email to TC: ", transaction.transactionCoordinator.email);
        sendEmail(
          [transaction.transactionCoordinator.email],
          [""],
          "signingCompleteToAgent",
          {
            recipientFirstName:
              transaction.transactionCoordinator.firstName || "",
            loginUrlUser: urlApp,
            senderFirstName: sender.firstName,
            senderLastName: sender.lastName,
            senderEmail: sender.email,
            transactionAddress: transactionAddress,
            documentName: document.name || "",
          }
        );
      }
    } else {
      console.log("No emails for this user");
    }
  } else {
    console.log("No emails for this environment");
  }
}

export async function sendDocSignedByClientToAgentEmail(sender, transaction, document, allParties) {
  if (process.env.REACT_APP_ENABLE_EMAILS === "true") {
    console.log("Emails are live for this environment");
    if (sender?.isTester !== true) {
      console.log("Emails are live for this user");
      const urlApp =
        process.env.REACT_APP_ENVIRONMENT === "development"
          ? "http://localhost:3000"
          : "https://app.transactioner.com";

      const transactionAddress = transaction?.address
        ? convertAddressStreetAndUnit(transaction.address)
        : "";

      // Find the agent who represents this client
      let agentToNotify = null;
      const senderRole = sender.role;

      if (senderRole?.startsWith("Buyer")) {
        // Find Buyer Agent
        agentToNotify = allParties.find(party => party.role === "Buyer Agent" || party.role === "Buyer Agent 2");
      } else if (senderRole?.startsWith("Seller")) {
        // Find Listing Agent
        agentToNotify = allParties.find(party => party.role === "Listing Agent" || party.role === "Listing Agent 2");
      }

      if (agentToNotify && agentToNotify.email) {
        // Send email to the agent
        // console.log("sendDocSignedByClientToAgentEmail::: Sending email to: ", agentToNotify.email);
        sendEmail([agentToNotify.email], [""], "signingCompleteToAgent", {
          recipientFirstName: agentToNotify.firstName || "",
          loginUrlUser: urlApp,
          senderFirstName: sender.firstName,
          senderLastName: sender.lastName,
          senderEmail: sender.email,
          transactionAddress: transactionAddress,
          documentName: document.name || "",
        });
      }

      // Also notify TransActioner user (agent), assistants, and TC
      let agentEmail = transaction.agentEmail
        ? transaction.agentEmail
        : transaction.agentProfile?.email
        ? transaction.agentProfile?.email
        : "";
      const agentIsTester = transaction.agentProfile?.isTester ? true : false;

      if (agentEmail && !agentIsTester) {
        let assistantEmail = "";
        if (transaction.assistantEmail) {
          assistantEmail = transaction.assistantEmail;
        }
        const agentFirstName = transaction.agentName
          ? transaction.agentName.split(" ")[0]
          : transaction.agentProfile?.firstName
          ? transaction.agentProfile.firstName
          : "";

          // console.log("sendDocSignedByClientToAgentEmail::: Sending email to: ", agentEmail, assistantEmail);
        sendEmail([agentEmail], [assistantEmail], "signingCompleteToAgent", {
          recipientFirstName: agentFirstName,
          loginUrlUser: urlApp,
          senderFirstName: sender.firstName,
          senderLastName: sender.lastName,
          senderEmail: sender.email,
          transactionAddress: transactionAddress,
          documentName: document.name || "",
        });

        // Notify TC if exists
        if (transaction.hasTc && transaction.transactionCoordinator?.email) {
          sendEmail(
            [transaction.transactionCoordinator.email],
            [""],
            "signingCompleteToAgent",
            {
              recipientFirstName:
                transaction.transactionCoordinator.firstName || "",
              loginUrlUser: urlApp,
              senderFirstName: sender.firstName,
              senderLastName: sender.lastName,
              senderEmail: sender.email,
              transactionAddress: transactionAddress,
              documentName: document.name || "",
            }
          );
        }
      }
    } else {
      console.log("No emails for this user");
    }
  } else {
    console.log("No emails for this environment");
  }
}

export async function sendCustomDocSharingEmail(
  partyList,
  type,
  sender,
  transaction,
  customSubject,
  customMessage,
  additionalCcEmails = [],
  additionalBccEmails = []
) {
  if (process.env.REACT_APP_ENABLE_EMAILS === "true") {
    console.log("Emails are live for this environment");
    if (sender?.isTester !== true) {
      console.log("Emails are live for this user");
      const urlApp =
        process.env.REACT_APP_ENVIRONMENT === "development"
          ? "http://localhost:3000"
          : "https://app.transactioner.com";

      const customMessageWithLineBreaks = customMessage.replace(/\n/g, "<br>");
      for (const party of partyList) {
        // Skip the 10-minute throttle for custom emails
        const partyEmailAutofill = party.role === "Buyer" || party.role === "Buyer 2" || party.role === "Buyer 3"
          || party.role?.startsWith("Seller") || party.role === "Seller 2" || party.role === "Seller 3"
          ? "&partyEmailAutofill=" + party.email : "";

        let loginUrlParty = `${urlApp}/partyLogin?partyFirstName=${
          party.firstName || ""
        }&partyLastName=${party.lastName || ""}&agentName=${
          sender.firstName + "%20" + sender.lastName + partyEmailAutofill
        }`;
        const loginUrlUser = urlApp;
        let assistantEmail = "";
        if (party.hasAssistant && party.assistant?.email) {
          assistantEmail = party.assistant?.email;
          loginUrlParty = loginUrlParty + "&assistant=true";
        }
        const transactionAddress = transaction?.address
          ? convertAddressStreetAndUnit(transaction.address)
          : "";
        let partyLoginUrlAllButTc = loginUrlParty;
        if (partyIsAgent(party.role) && party.isUser && party.isLinked) {
          partyLoginUrlAllButTc = loginUrlUser;
        }

        // Construct custom email content
        const emailBody = `${customMessage}`;
        // The brokerage logo belongs to the transaction, not the sender, so it
        // shows regardless of who actually sent the email.
        const agentBrokerageLogoRef = transaction.agentProfile?.brokerageLogoRef?.length > 0 ? "<p><img height='70px' src='" + transaction.agentProfile.brokerageLogoRef + "'/></p>" : "";

        let replyTo = sender.email || "help@transactioner.com";
        let from = `${sender.firstName} ${sender.lastName} <help@transactioner.com>`;

        const mailRef = collection(db, "mail");
        // Combine party assistant email with additional CC emails
        const ccEmails = [
          ...(assistantEmail ? [assistantEmail] : []),
          ...additionalCcEmails.filter(email => email && email !== party.email),
        ];
        // Combine default BCC with additional BCC emails
        const baseBcc = process.env.REACT_APP_ENVIRONMENT === "development"
          ? ["emaildev@transactioner.com"]
          : ["emailprod@transactioner.com"];
        const bccEmails = [
          ...baseBcc,
          ...additionalBccEmails.filter(email => email && email !== party.email),
        ];
        const mailOptions = {
          to: [party.email],
          cc: ccEmails,
          bcc: bccEmails,
          from: from,
          replyTo: replyTo,
          message: {
            subject: customSubject,
            text: emailBody,
            html: `
              <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                <div style="text-align: center; padding: 20px; background-color: #f8f9fa;">
                  <img align="center" alt=""
											src="https://mcusercontent.com/3629975ce1b1a834a71472288/images/559fbb84-f639-2c0f-c514-4cb45906b25a.png"
											width="126.83999999999999"
											style="max-width:302px; padding-bottom: 0; display: inline !important; vertical-align: bottom;" class="mcnImage" />
									<p class="mcnTextContent" style="text-align: center; margin-top: -10px">Modern Real Estate Transaction Management</p>
                 </div>
                <div style="text-align: center; padding: 20px; background-color: #f8f9fa;">
                  <h2 style="color: #333; margin: 0;">${customSubject}</h2>
                </div>
                <div style="padding: 20px; background-color: white;">
                  <div style="text-align: center; margin-bottom: 20px;">
                    <a href="${partyLoginUrlAllButTc}" style="background-color: #007bff; color: white; padding: 12px 24px; text-decoration: none; border-radius: 5px; display: inline-block;">View Documents</a>
                  </div>
                  <div style="text-align: center; margin-bottom: 20px;">
                    <strong>${transactionAddress}</strong>
                  </div>
                  <div style="line-height: 1.6;">
                    <p>${customMessageWithLineBreaks}</p>
                    ${agentBrokerageLogoRef}
                  </div>
                </div>
              </div>
            `
          },
          createdAt: serverTimestamp(),
        };

        try {
          if (process.env.REACT_APP_ENVIRONMENT === "development") {
            console.log("CUSTOM EMAIL THAT WOULD BE SENT: ", mailOptions);
          } else {
            await addDoc(mailRef, mailOptions);
          }
        } catch (error) {
          console.error("Error sending custom email:", error);
          throw error;
        }

        // Send to Transaction Coordinator if applicable
        if (
          partyIsAgent(party.role) &&
          party.hasTransactionCoordinator &&
          party.transactionCoordinator?.email
        ) {
          let partyLoginUrlTc = loginUrlParty;
          if (party.isLinked && party.transactionCoordinator.isUser) {
            partyLoginUrlTc = loginUrlUser;
          }

          const tcEmailBody = `Hi ${party.transactionCoordinator?.firstName || 'there'},
The following email was sent to ${party.firstName || 'the other party'}:

${customMessage}
`;

          const tcMailOptions = {
            to: [party.transactionCoordinator?.email],
            bcc: process.env.REACT_APP_ENVIRONMENT === "development"
              ? ["emaildev@transactioner.com"]
              : ["emailprod@transactioner.com"],
            from: from,
            replyTo: replyTo,
            message: {
              subject: customSubject,
              text: tcEmailBody,
              html: `
                <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                  <div style="text-align: center; padding: 20px; background-color: #f8f9fa;">
                  <img align="center" alt=""
											src="https://mcusercontent.com/3629975ce1b1a834a71472288/images/559fbb84-f639-2c0f-c514-4cb45906b25a.png"
											width="126.83999999999999"
											style="max-width:302px; padding-bottom: 0; display: inline !important; vertical-align: bottom;" class="mcnImage" />
									<p class="mcnTextContent" style="text-align: center; margin-top: -10px">Modern Real Estate Transaction Management</p>
                 </div>
                  <div style="text-align: center; padding: 20px; background-color: #f8f9fa;">
                    <h2 style="color: #333; margin: 0;">${customSubject}</h2>
                  </div>
                  <div style="padding: 20px; background-color: white;">
                    <div style="text-align: center; margin-bottom: 20px;">
                      <a href="${partyLoginUrlTc}" style="background-color: #007bff; color: white; padding: 12px 24px; text-decoration: none; border-radius: 5px; display: inline-block;">View Documents</a>
                    </div>
                    <div style="text-align: center; margin-bottom: 20px;">
                      <strong>${transactionAddress}</strong>
                    </div>
                    <div style="line-height: 1.6;">
                      <p>Hi ${party.transactionCoordinator?.firstName || 'there'},</p>
                      <p>The following email was sent to ${party.firstName || 'the other party'}:</p>
                      <p>${customMessageWithLineBreaks}</p>
                      ${agentBrokerageLogoRef}
                    </div>
                  </div>
                </div>
              `
            },
            createdAt: serverTimestamp(),
          };

          try {
            if (process.env.REACT_APP_ENVIRONMENT === "development") {
              console.log("CUSTOM TC EMAIL THAT WOULD BE SENT: ", tcMailOptions);
            } else {
              await addDoc(mailRef, tcMailOptions);
            }
          } catch (error) {
            console.error("Error sending custom TC email:", error);
          }
        }

        // Send to CoAgent if applicable
        if (
          partyIsAgent(party.role) &&
          party.hasCoAgent &&
          party.coAgent?.email
        ) {
          let partyLoginUrlCoAgent = loginUrlParty;
          if (party.isLinked && party.coAgent?.isUser) {
            partyLoginUrlCoAgent = loginUrlUser;
          }

          const coAgentEmailBody = `Hi ${party.coAgent?.firstName || 'there'},
          The following email was sent to ${party.firstName || 'the other party'}:

${customMessage}
`;

          const coAgentMailOptions = {
            to: [party.coAgent?.email],
            bcc: process.env.REACT_APP_ENVIRONMENT === "development"
              ? ["emaildev@transactioner.com"]
              : ["emailprod@transactioner.com"],
            from: from,
            replyTo: replyTo,
            message: {
              subject: customSubject,
              text: coAgentEmailBody,
              html: `
                <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                  <div style="text-align: center; padding: 20px; background-color: #f8f9fa;">
                  <img align="center" alt=""
											src="https://mcusercontent.com/3629975ce1b1a834a71472288/images/559fbb84-f639-2c0f-c514-4cb45906b25a.png"
											width="126.83999999999999"
											style="max-width:302px; padding-bottom: 0; display: inline !important; vertical-align: bottom;" class="mcnImage" />
									<p class="mcnTextContent" style="text-align: center; margin-top: -10px">Modern Real Estate Transaction Management</p>
                 </div>
                <div style="text-align: center; padding: 20px; background-color: #f8f9fa;">
                  <h2 style="color: #333; margin: 0;">${customSubject}</h2>
                </div>
                  <div style="padding: 20px; background-color: white;">
                    <div style="text-align: center; margin-bottom: 20px;">
                      <a href="${partyLoginUrlCoAgent}" style="background-color: #007bff; color: white; padding: 12px 24px; text-decoration: none; border-radius: 5px; display: inline-block;">View Documents</a>
                    </div>
                    <div style="text-align: center; margin-bottom: 20px;">
                      <strong>${transactionAddress}</strong>
                    </div>
                    <div style="line-height: 1.6;">
                      <p>Hi ${party.coAgent?.firstName || 'there'},</p>
                      <p>The following email was sent to ${party.firstName || 'the other party'}:</p>
                      <p>${customMessageWithLineBreaks}</p>
                      ${agentBrokerageLogoRef}
                    </div>
                  </div>
                </div>
              `
            },
            createdAt: serverTimestamp(),
          };

          try {
            if (process.env.REACT_APP_ENVIRONMENT === "development") {
              console.log("CUSTOM COAGENT EMAIL THAT WOULD BE SENT: ", coAgentMailOptions);
            } else {
              await addDoc(mailRef, coAgentMailOptions);
            }
          } catch (error) {
            console.error("Error sending custom CoAgent email:", error);
          }
        }
      }
    } else {
      console.log("No emails for this user");
    }
  } else {
    console.log("No emails for this environment");
  }
}

/**
 * Build the default (non-custom) subject/message copy for a sharing or
 * sendForSigning email. This is the literal text that gets sent AND logged to
 * Email History — kept in one place so the two can never drift apart.
 * Sharing intentionally doesn't name a specific document: the 10-minute
 * send throttle means one email can cover several documents shared in
 * quick succession, so the copy stays generic ("new documents").
 */
function buildDefaultSharingEmailCopy(type, doc, transactionAddress) {
  if (type === "signing") {
    const docName = doc?.name || "a document";
    const addressLine = transactionAddress ? ` for ${transactionAddress}` : "";
    return {
      subject: `Your signature is requested on "${docName}"`,
      message: `You have a document ready for your signature${addressLine}: "${docName}". Please click the button above (or go to https://app.transactioner.com/partyLogin) to view and sign it.
      Please let me know if you have any questions.`,
    };
  }
  const addressLine = transactionAddress ? ` for ${transactionAddress}` : "";
  return {
    subject: `New documents have been shared with you${addressLine}`,
    message: `I'm sharing new documents with you that you can view by clicking the "View Documents" button above or go to https://app.transactioner.com/partyLogin.
    Please let me know if you have any questions.`,
  };
}

/**
 * Role label for someone sending on a transaction who isn't the primary agent —
 * matched by email against the transaction's known team members, same
 * precedence as the CC/BCC options in DocPendingNotificationModal.
 */
function getSenderRoleOnTransaction(sender, transaction) {
  const senderEmail = sender?.email?.trim().toLowerCase();
  if (!senderEmail) return "";
  const matches = (email) => !!email && email.trim().toLowerCase() === senderEmail;

  if (matches(transaction?.coAgent?.email)) return "CoAgent";
  if (
    matches(transaction?.transactionCoordinator?.email) ||
    matches(transaction?.agentAsTransactionCoordinator?.email)
  ) {
    return "TC";
  }
  const assistants = Array.isArray(transaction?.assistant) ? transaction.assistant : [];
  if (assistants.some((a) => matches(a?.email))) return "Assistant";
  if (matches(transaction?.managerDetails?.managerEmail)) return "Manager";
  const managerAssistants = Array.isArray(transaction?.agentProfile?.managerAssistants)
    ? transaction.agentProfile.managerAssistants
    : [];
  if (managerAssistants.some((ma) => matches(ma?.email))) return "Manager Assistant";
  return "";
}

/**
 * "Sent by ... [role]" block used instead of the usual "Thanks," sign-off when
 * someone other than the transaction's primary agent shares/sends a document
 * (coagent, TC, assistant, manager, manager assistant, etc.). Lists the actual
 * agent (and coagent, if any) on record underneath so the recipient still
 * knows who represents them.
 */
function buildOnBehalfSignature(sender, transaction) {
  const senderName = `${sender?.firstName || ""} ${sender?.lastName || ""}`.trim();
  const role = getSenderRoleOnTransaction(sender, transaction);
  const sentByLine = `Sent by ${senderName || "a team member"}${role ? ` [${role}]` : ""}`;

  const sideLabel = transaction?.agentRepresents === "Buyer" ? "Buyer" : "Listing";
  const agent = transaction?.agentProfile;
  const agentName = `${agent?.firstName || ""} ${agent?.lastName || ""}`.trim();
  const lines = [];
  if (agentName) {
    const contact = [agent?.phone, agent?.email].filter(Boolean).join(" ");
    lines.push(`${sideLabel} Agent: ${agentName}${contact ? ` ${contact}` : ""}`);
  }

  const coAgent = transaction?.coAgent;
  const coAgentName = `${coAgent?.firstName || ""} ${coAgent?.lastName || ""}`.trim();
  if (transaction?.coAgentId && coAgentName) {
    const contact = [coAgent?.phone, coAgent?.email].filter(Boolean).join(" ");
    lines.push(`CoAgent: ${coAgentName}${contact ? ` ${contact}` : ""}`);
  }

  if (lines.length === 0) {
    return { html: `<p>${sentByLine}</p>`, text: `\n\n${sentByLine}` };
  }
  return {
    html: `<p>${sentByLine}</p><p>${lines.join("<br/>")}</p>`,
    text: `\n\n${sentByLine}\n\n${lines.join("\n")}`,
  };
}

/**
 * Sign-off block appended to the body of every default sharing/signing email
 * so the recipient knows who to reach out to. Normally "Thanks," signed by
 * whoever actually sent it. But when that's someone other than the
 * transaction's primary agent — a coagent, TC, assistant, manager, or manager
 * assistant sharing/sending on the agent's behalf — sign with "Sent by ...
 * [role]" and list the actual agent (and coagent) on record instead, so the
 * recipient isn't misled about who represents them.
 */
export function buildSenderSignature(sender, transaction) {
  const senderEmail = sender?.email?.trim().toLowerCase();
  const primaryAgentEmail = transaction?.agentProfile?.email?.trim().toLowerCase();
  const isPrimaryAgent =
    !!senderEmail && !!primaryAgentEmail && senderEmail === primaryAgentEmail;

  if (!isPrimaryAgent && transaction?.agentProfile?.lastName) {
    return buildOnBehalfSignature(sender, transaction);
  }

  const name = `${sender?.firstName || ""} ${sender?.lastName || ""}`.trim();
  const contactLines = [sender?.email, sender?.phone].filter(Boolean);
  const lines = [name, ...contactLines].filter(Boolean);
  if (lines.length === 0) return { html: "", text: "" };
  return {
    html: `<p>Thanks,<br/>${lines.join("<br/>")}</p>`,
    text: `\n\nThanks,\n${lines.join("\n")}`,
  };
}

/**
 * Build the HTML wrapper used for both the primary recipient and any forwarded
 * copy sent to a TC/CoAgent. Mirrors the layout used for custom sharing emails
 * (sendCustomDocSharingEmail) so branding stays consistent across both.
 */
export function buildSharingEmailHtml({
  subject,
  bodyHtml,
  loginUrl,
  transactionAddress,
  brokerageLogoRef,
}) {
  return `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
      <div style="text-align: center; padding: 20px; background-color: #f8f9fa;">
        <img align="center" alt=""
          src="https://mcusercontent.com/3629975ce1b1a834a71472288/images/559fbb84-f639-2c0f-c514-4cb45906b25a.png"
          width="126.83999999999999"
          style="max-width:302px; padding-bottom: 0; display: inline !important; vertical-align: bottom;" class="mcnImage" />
        <p class="mcnTextContent" style="text-align: center; margin-top: -10px">Modern Real Estate Transaction Management</p>
      </div>
      <div style="text-align: center; padding: 20px; background-color: #f8f9fa;">
        <h2 style="color: #333; margin: 0;">${subject}</h2>
      </div>
      <div style="padding: 20px; background-color: white;">
        <div style="text-align: center; margin-bottom: 20px;">
          <a href="${loginUrl}" style="background-color: #007bff; color: white; padding: 12px 24px; text-decoration: none; border-radius: 5px; display: inline-block;">View Documents</a>
        </div>
        <div style="text-align: center; margin-bottom: 20px;">
          <strong>${transactionAddress || ""}</strong>
        </div>
        <div style="line-height: 1.6;">
          ${bodyHtml}
          ${brokerageLogoRef || ""}
        </div>
      </div>
    </div>
  `;
}

export async function sendDocSharingEmail(
  partyList,
  type,
  sender,
  transaction,
  doc,
  restrictedToPartyId,
  agentContextLines
) {
  const emailsLive = process.env.REACT_APP_ENABLE_EMAILS === "true";
  const isTester = sender?.isTester === true;
  console.log(
    emailsLive ? "Emails are live for this environment" : "No emails for this environment"
  );
  console.log(isTester ? "No emails for this user" : "Emails are live for this user");

  const sendingTimeThreshold = subMinutes(new Date(), 10);
  const urlApp =
    process.env.REACT_APP_ENVIRONMENT === "development"
      ? "http://localhost:3000"
      : "https://app.transactioner.com";

  partyList.forEach(async (party) => {
    if (
      !party.lastEmailSentAt ||
      new Date(party.lastEmailSentAt) < sendingTimeThreshold
    ) {
      const partyEmailAutofill = party.role === "Buyer" || party.role === "Buyer 2" || party.role === "Buyer 3"
        || party.role?.startsWith("Seller") || party.role === "Seller 2" || party.role === "Seller 3"
        ? "&partyEmailAutofill=" + party.email : "";

      let loginUrlParty = `${urlApp}/partyLogin?partyFirstName=${
        party.firstName || ""
      }&partyLastName=${party.lastName || ""}&agentName=${
        sender.firstName + "%20" + sender.lastName + partyEmailAutofill
      }`;
      const loginUrlUser = urlApp;
      let assistantEmail = "";
      if (party.hasAssistant && party.assistant?.email) {
        assistantEmail = party.assistant?.email;
        loginUrlParty = loginUrlParty + "&assistant=true";
      }
      const transactionAddress = transaction?.address
        ? convertAddressStreetAndUnit(transaction.address)
        : "";
      let partyLoginUrlAllButTc = loginUrlParty;
      if (partyIsAgent(party.role) && party.isUser && party.isLinked) {
        partyLoginUrlAllButTc = loginUrlUser;
      }

      // Build the actual subject/message content up front — this is the literal
      // text that gets both sent (when live) and logged to Email History, so the
      // two can never drift apart.
      const recipientFirstName = party.firstName || "there";
      const senderName = `${sender.firstName || ""} ${
        sender.lastName || ""
      }`.trim();
      const { subject, message } = buildDefaultSharingEmailCopy(
        type,
        doc,
        transactionAddress
      );
      // restrictedToPartyId marks the AgentShare/AgentSendForSigning party-portal
      // flow, where `transaction` belongs to the other side and agentContextLines
      // (below) already covers who the real agent/CoAgent are — so skip the
      // transaction-based on-behalf signature there and just sign as the sender.
      const signature = buildSenderSignature(
        sender,
        restrictedToPartyId ? null : transaction
      );
      // When a CoAgent or TC sends on the agent's behalf (see AgentShare /
      // AgentSendForSigning), show who the agent (and CoAgent) on record are,
      // since the signature above is whoever actually sent this, not them.
      const agentContextHtml = agentContextLines?.length
        ? `<p>${agentContextLines.join("<br/>")}</p>`
        : "";
      const agentContextText = agentContextLines?.length
        ? `\n\n${agentContextLines.join("\n")}`
        : "";
      const replyTo = sender.email || "help@transactioner.com";
      const from = `${sender.firstName || ""} ${
        sender.lastName || ""
      } <help@transactioner.com>`;
      // The brokerage logo belongs to the transaction, not the sender, so it
      // shows regardless of who actually sent the email — except in the
      // AgentShare/AgentSendForSigning party-portal flow (restrictedToPartyId),
      // where the sender is a non-user agent on the other side of the deal,
      // typically at a different brokerage than the transaction's own agent.
      const brokerageLogoRef =
        !restrictedToPartyId && transaction.agentProfile?.brokerageLogoRef?.length > 0
          ? "<p><img height='70px' src='" +
            transaction.agentProfile.brokerageLogoRef +
            "'/></p>"
          : "";
      const toEmailsAndRoles = [
        {
          role: party.role || "",
          email: party.email,
          firstName: party.firstName || "",
          lastName: party.lastName || "",
        },
      ];
      const ccEmailsAndRoles = [];

      // Built up front (not just inside the live-send branch) so the exact same
      // markup — logo, "View Documents" button, brokerage logo — is what gets
      // logged to Email History, whether or not the email actually goes out.
      const html = buildSharingEmailHtml({
        subject,
        bodyHtml: `<p>Hi ${recipientFirstName},</p><p>${message}</p>${agentContextHtml}${signature.html}`,
        loginUrl: partyLoginUrlAllButTc,
        transactionAddress,
        brokerageLogoRef,
      });

      if (emailsLive && !isTester) {
        const mailRef = collection(db, "mail");
        const mailOptions = {
          to: [party.email],
          cc: assistantEmail ? [assistantEmail] : [],
          bcc: [
            process.env.REACT_APP_ENVIRONMENT === "development"
              ? "emaildev@transactioner.com"
              : "emailprod@transactioner.com",
          ],
          from,
          replyTo,
          message: {
            subject,
            text: `Hi ${recipientFirstName},\n\n${message}${agentContextText}${signature.text}\n\n${partyLoginUrlAllButTc}`,
            html,
          },
          createdAt: serverTimestamp(),
        };

        try {
          if (process.env.REACT_APP_ENVIRONMENT === "development") {
            console.log("SHARING EMAIL THAT WOULD BE SENT: ", mailOptions);
          } else {
            await addDoc(mailRef, mailOptions);
          }
        } catch (error) {
          console.error("Error sending doc sharing email:", error);
        }

        if (
          partyIsAgent(party.role) &&
          party.hasTransactionCoordinator &&
          party.transactionCoordinator?.email
        ) {
          let partyLoginUrlTc = loginUrlParty;
          if (party.isLinked && party.transactionCoordinator.isUser) {
            partyLoginUrlTc = loginUrlUser;
          }
          const tcFirstName = party.transactionCoordinator?.firstName || "there";
          const tcMailOptions = {
            to: [party.transactionCoordinator?.email],
            bcc: [
              process.env.REACT_APP_ENVIRONMENT === "development"
                ? "emaildev@transactioner.com"
                : "emailprod@transactioner.com",
            ],
            from,
            replyTo,
            message: {
              subject,
              text: `Hi ${tcFirstName},\nThe following email was sent to ${party.firstName || "the other party"}:\n\n${message}${agentContextText}${signature.text}\n\n${partyLoginUrlTc}`,
              html: buildSharingEmailHtml({
                subject,
                bodyHtml: `<p>Hi ${tcFirstName},</p><p>The following email was sent to ${party.firstName || "the other party"}:</p><p>${message}</p>${agentContextHtml}${signature.html}`,
                loginUrl: partyLoginUrlTc,
                transactionAddress,
                brokerageLogoRef,
              }),
            },
            createdAt: serverTimestamp(),
          };
          try {
            if (process.env.REACT_APP_ENVIRONMENT === "development") {
              console.log("SHARING TC EMAIL THAT WOULD BE SENT: ", tcMailOptions);
            } else {
              await addDoc(mailRef, tcMailOptions);
            }
            ccEmailsAndRoles.push({
              role: "TC",
              email: party.transactionCoordinator.email,
              firstName: party.transactionCoordinator?.firstName || "",
              lastName: party.transactionCoordinator?.lastName || "",
            });
          } catch (error) {
            console.error("Error sending doc sharing TC email:", error);
          }
        }

        if (
          partyIsAgent(party.role) &&
          party.hasCoAgent &&
          party.coAgent?.email
        ) {
          let partyLoginUrlCoAgent = loginUrlParty;
          if (party.isLinked && party.coAgent?.isUser) {
            partyLoginUrlCoAgent = loginUrlUser;
          }
          const coAgentFirstName = party.coAgent?.firstName || "there";
          const coAgentMailOptions = {
            to: [party.coAgent?.email],
            bcc: [
              process.env.REACT_APP_ENVIRONMENT === "development"
                ? "emaildev@transactioner.com"
                : "emailprod@transactioner.com",
            ],
            from,
            replyTo,
            message: {
              subject,
              text: `Hi ${coAgentFirstName},\nThe following email was sent to ${party.firstName || "the other party"}:\n\n${message}${agentContextText}${signature.text}\n\n${partyLoginUrlCoAgent}`,
              html: buildSharingEmailHtml({
                subject,
                bodyHtml: `<p>Hi ${coAgentFirstName},</p><p>The following email was sent to ${party.firstName || "the other party"}:</p><p>${message}</p>${agentContextHtml}${signature.html}`,
                loginUrl: partyLoginUrlCoAgent,
                transactionAddress,
                brokerageLogoRef,
              }),
            },
            createdAt: serverTimestamp(),
          };
          try {
            if (process.env.REACT_APP_ENVIRONMENT === "development") {
              console.log("SHARING COAGENT EMAIL THAT WOULD BE SENT: ", coAgentMailOptions);
            } else {
              await addDoc(mailRef, coAgentMailOptions);
            }
            ccEmailsAndRoles.push({
              role: "CoAgent",
              email: party.coAgent.email,
              firstName: party.coAgent?.firstName || "",
              lastName: party.coAgent?.lastName || "",
            });
          } catch (error) {
            console.error("Error sending doc sharing CoAgent email:", error);
          }
        }

        // Guard against callers passing a lightweight client object with no
        // party doc id (e.g. a freshly-built "signingRequestedFor" entry) —
        // updatePartyInDb/doc() throws on an undefined id, which would
        // otherwise crash this as an unhandled rejection since forEach
        // doesn't propagate it back to the caller's await.
        if (party.id) {
          updatePartyInDb(party, {
            lastEmailSentAt: new Date(),
          });
        }
      } else {
        console.log(
          emailsLive
            ? "No emails for this user"
            : "No emails for this environment"
        );
      }

      // Record the exact subject/message in Email History — not gated by
      // emailsLive/isTester (same as the custom email history logging) so the
      // log reflects the action taken, matching the parallel addHistoryToDb
      // entry ("shared" / "sent for signing") made alongside it.
      try {
        await addEmailToDb(
          {
            transactionId: transaction?.id || "",
            transactionTitle: transaction?.title || "",
            from: sender?.email || "",
            fromName: senderName,
            toEmails: toEmailsAndRoles.map((r) => r.email),
            toEmailsAndRoles,
            ccEmailsAndRoles,
            subject,
            message: {
              subject,
              text: `Hi ${recipientFirstName},\n\n${message}${agentContextText}${signature.text}`,
              html,
            },
            documentName: doc?.name || "",
            type:
              type === "signing"
                ? "sendForSigning-notification"
                : "sharing-notification",
            sentAt: serverTimestamp(),
            ...(restrictedToPartyId ? { restrictedToPartyId } : {}),
          },
          {
            message: {
              subject,
              text: `Hi ${recipientFirstName},\n\n${message}${agentContextText}${signature.text}`,
              html,
            },
          }
        );
      } catch (error) {
        console.log("Error recording doc sharing email history:", error);
      }
    }
  });
}

/**
 * Record a "sharing" or "sendForSigning" email in the Email History log, for callers
 * that sent a custom-worded email themselves (via sendCustomDocSharingEmail, e.g. a
 * default email template) — pass the same customSubject/customMessage that was
 * actually sent so the log matches word-for-word. (The plain, non-custom path doesn't
 * need this: sendDocSharingEmail logs its own exact content internally.)
 * This is intentionally not gated by REACT_APP_ENABLE_EMAILS/isTester (same as the
 * rest of the custom email history logging) so the log reflects the action taken,
 * matching the parallel addHistoryToDb entry ("shared" / "sent for signing") made
 * alongside it.
 * Pass restrictedToPartyId when the sender is a party-portal agent sharing with their
 * own clients — those emails are internal to that agent's own side and must not show
 * up in the main transaction's Email History, which the other side of the transaction
 * can see. They're only ever fetched back out via fetchPartyEmailHistoryFromDb, scoped
 * to that same partyId.
 */
export async function logDocSharingEmailHistory(
  partyList,
  type,
  sender,
  transaction,
  doc,
  customSubject,
  customMessage,
  restrictedToPartyId
) {
  if (!Array.isArray(partyList) || partyList.length === 0) return;

  const toEmailsAndRoles = partyList
    .filter((party) => party?.email)
    .map((party) => ({
      role: party.role || "",
      email: party.email,
      firstName: party.firstName || "",
      lastName: party.lastName || "",
    }));

  if (toEmailsAndRoles.length === 0) return;

  const transactionAddress = transaction?.address
    ? convertAddressStreetAndUnit(transaction.address)
    : "";
  const addressOrTitle = transactionAddress || transaction?.title || "";
  const docName = doc?.name || "the document";
  const senderName = `${sender?.firstName || ""} ${sender?.lastName || ""}`.trim();

  const subject =
    customSubject ||
    (type === "signing"
      ? `Your signature is requested on "${docName}"`
      : `${docName} has been shared with you`);

  const message =
    customMessage ||
    (type === "signing"
      ? `${senderName || "Your agent"} sent "${docName}" to you for signing${
          addressOrTitle ? ` regarding ${addressOrTitle}` : ""
        }.`
      : `${senderName || "Your agent"} shared "${docName}" with you${
          addressOrTitle ? ` regarding ${addressOrTitle}` : ""
        }.`);

  // Rebuild the same branded wrapper (logo, "View Documents" button, brokerage
  // logo) sendCustomDocSharingEmail actually sent, so the log shows it too.
  // The login link here is generic (not the recipient-personalized deep link
  // sendCustomDocSharingEmail used) since this is a display/reference copy.
  const urlApp =
    process.env.REACT_APP_ENVIRONMENT === "development"
      ? "http://localhost:3000"
      : "https://app.transactioner.com";
  // The brokerage logo belongs to the transaction, not the sender, so it shows
  // regardless of who actually sent the email — except for a party-portal
  // sender (restrictedToPartyId), typically at a different brokerage.
  const brokerageLogoRef =
    !restrictedToPartyId && transaction.agentProfile?.brokerageLogoRef?.length > 0
      ? "<p><img height='70px' src='" +
        transaction.agentProfile.brokerageLogoRef +
        "'/></p>"
      : "";
  // restrictedToPartyId marks a party-portal sender (see doc comment above) whose
  // `transaction` belongs to the other side, so skip the transaction-based
  // on-behalf signature there and just sign as the sender.
  const signature = buildSenderSignature(sender, restrictedToPartyId ? null : transaction);
  const recipientFirstName =
    toEmailsAndRoles.length === 1 ? toEmailsAndRoles[0].firstName || "there" : "there";
  const html = buildSharingEmailHtml({
    subject,
    bodyHtml: `<p>Hi ${recipientFirstName},</p><p>${message}</p>${signature.html}`,
    loginUrl: urlApp,
    transactionAddress,
    brokerageLogoRef,
  });
  const text = `Hi ${recipientFirstName},\n\n${message}${signature.text}`;

  try {
    await addEmailToDb(
      {
        transactionId: transaction?.id || "",
        transactionTitle: transaction?.title || "",
        from: sender?.email || "",
        fromName: senderName,
        toEmails: toEmailsAndRoles.map((r) => r.email),
        toEmailsAndRoles,
        subject,
        message: { subject, text, html },
        documentName: docName,
        type: type === "signing" ? "sendForSigning-notification" : "sharing-notification",
        sentAt: serverTimestamp(),
        ...(restrictedToPartyId ? { restrictedToPartyId } : {}),
      },
      { message: { subject, text, html } }
    );
  } catch (error) {
    console.log("Error recording doc sharing email history:", error);
  }
}

export async function sendTransactionEmail(
  values,
  emailFields,
  sender,
  transaction
) {
  if (process.env.REACT_APP_ENABLE_EMAILS === "true") {
    console.log("Emails are live for this environment");
    if (sender?.isTester !== true) {
      console.log("Emails are live for this user");

      const to = emailFields.toEmails ? emailFields.toEmails.toString() : null;
      const cc = emailFields.ccEmails ? emailFields.ccEmails.toString() : null;
      const bcc = emailFields.bccEmails
        ? emailFields.bccEmails.toString()
        : null;
      let from = "";
      
      let replyTo = sender.email || "help@transactioner.com";
      from = `${sender.firstName} ${sender.lastName} <help@transactioner.com>`;
    

      const mailRef = collection(db, "mail");
      const mailOptions = {
        to: to,
        cc: cc,
        bcc: bcc,
        from: from,
        replyTo: replyTo,
        message: {
          subject: values.subject,
          text: values.message,
        },
        createdAt: serverTimestamp(),
      };
      try {
        if (process.env.REACT_APP_ENVIRONMENT === "development") {
          console.log("EMAIL THAT WOULD BE SENT: ", mailOptions);
          return;
        } else {
          return addDoc(mailRef, mailOptions);
        }
      } catch (error) {
        throw error;
      }
    } else {
      console.log("No emails for this user");
    }
  } else {
    console.log("No emails for this environment");
  }
}

export async function sendEmail(email, assistantEmail, templateName, data) {
  const mailRef = collection(db, "mail");
  let mailOptions = {};
  if (process.env.REACT_APP_ENVIRONMENT === "development") {
    console.log("sendEmail:: EMAIL WOULD GO TO: ", email, assistantEmail, "TEMPLATE: ", templateName);
    mailOptions = {
      to: "emaildev@transactioner.com",
      template: {
        name: templateName,
        data: data,
      },
      createdAt: serverTimestamp(),
    };
    if (!assistantEmail.includes("")) {
      mailOptions["cc"] = "emaildev@transactioner.com";
    }
  } else {
    mailOptions = {
      to: email,
      bcc: "emailprod@transactioner.com",
      template: {
        name: templateName,
        data: data,
      },
      createdAt: serverTimestamp(),
    };
    if (!assistantEmail.includes("")) {
      mailOptions["cc"] = assistantEmail;
    }
    if (data?.replyTo) {
      mailOptions["replyTo"] = data.replyTo;
    }
  }
  try {
    return addDoc(mailRef, mailOptions);
  } catch (error) {
    throw error;
  }
}

///////////////////////////////////////////////////////////////
///////////////////  EMAIL NOTIFICATIONS  /////////////////////
///////////////////////////////////////////////////////////////

// These three notification senders used to resolve recipients client-side by
// reading OTHER users' full users/{uid} profile documents directly from the
// browser (agent, manager, TC, agentAsTc, coAgent, assistants...). That's
// both fragile - it silently fails with "Missing or insufficient
// permissions" for any signed-in viewer whose own Firestore access doesn't
// extend to those other profiles, e.g. a party finishing a document-signing
// flow reading the transaction's agentAsTc profile just to notify them - and
// an unnecessarily broad data-exposure surface. The recipient lookup and
// email composition now run in Cloud Functions (functions/index.js) via the
// Admin SDK, which bypasses security rules for this internal bookkeeping.
// Signatures are kept identical so none of the call sites need to change.

/**
 * Send a "new transaction created" notification email to relevant transaction users
 * @param {Object} transaction - The transaction object
 * @param {Object} currentUserProfile - unused client-side; the server derives this
 *   from the caller's own auth token so it can't be spoofed.
 */
export async function sendNewTransactionNotificationEmail(transaction, currentUserProfile) {
  if (!transaction) return;
  try {
    await functionSendNewTransactionNotificationEmail({ transactionId: transaction.id });
  } catch (e) {
    console.log("Error sending new transaction notification emails:", e);
  }
}

/**
 * Send document signed notification emails to relevant transaction users
 * @param {Object} signer - The party who signed the document
 * @param {Object} transaction - The transaction object
 * @param {Object} document - The document that was signed
 */
export async function sendDocumentSignedNotificationEmails(signer, transaction, document) {
  if (!transaction || !signer || !document) return;
  try {
    await functionSendDocumentSignedNotificationEmails({
      transactionId: transaction.id,
      signer: {
        firstName: signer.firstName || "",
        lastName: signer.lastName || "",
        email: signer.email || "",
        isTester: signer.isTester === true,
      },
      documentName: document.name || "",
    });
  } catch (e) {
    console.log("Error sending document signed notification emails:", e);
  }
}

/**
 * Send transaction status changed notification emails to relevant transaction users
 * @param {Object} transaction - The transaction object
 * @param {string} oldStatus - The previous status
 * @param {string} newStatus - The new status
 * @param {Object} currentUserProfile - unused client-side; the server derives this
 *   from the caller's own auth token so it can't be spoofed.
 */
export async function sendTransactionStatusChangedNotificationEmails(transaction, oldStatus, newStatus, currentUserProfile) {
  if (!transaction) return;
  try {
    await functionSendTransactionStatusChangedNotificationEmails({
      transactionId: transaction.id,
      oldStatus: oldStatus || "",
      newStatus: newStatus || "",
    });
  } catch (e) {
    console.log("Error sending transaction status changed notification emails:", e);
  }
}
  
///////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////
///////////////////////////  HELP  ////////////////////////////
///////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////

export function getHelpQuestionsFromDb() {
  const helpCollection = collection(db, "help");
  return getDocs(helpCollection).then((snapshot) => {
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  });
}

export async function sendHelpQuestionEmail(userQuestion, systemAnswer, currentUserProfile, transaction) {
  if (process.env.REACT_APP_ENABLE_EMAILS !== "true") {
    console.log("No emails for this environment");
    return;
  }
  if (currentUserProfile?.isTester === true) {
    console.log("No emails for this user");
    return;
  }
  
  const emailData = {
    to: ["support@transactioner.com"],
    template: {
      name: "helpQuestion",
      data: {
        question: userQuestion,
        answer: systemAnswer,
        userEmail: currentUserProfile?.email || "Unknown user",
        userName: currentUserProfile
          ? `${currentUserProfile.firstName || ""} ${
              currentUserProfile.lastName || ""
            }`.trim()
          : "Unknown user",
        transactionInfo: transaction
          ? `${transaction.title || ""} - ${
              transaction.address
                ? convertAddressFull(transaction.address)
                : "No address"
            }`
          : "No active transaction",
      },
    },
  };

  const mailRef = collection(db, "mail");
  try {
    if (process.env.REACT_APP_ENVIRONMENT === "development") {
      console.log("EMAIL THAT WOULD BE SENT: ", emailData);
      return;
    } else {
      return addDoc(mailRef, emailData);
    }
  } catch (error) {
    throw error;
  }
}

// Sent when an agent who isn't yet a subscriber (detected or manually flagged on
// PartyAllOverview) fills out the free trial signup form. Notifies support so they can
// set up the account and reply with login information.
export async function sendFreeTrialSignupEmail(signupData, currentUserProfile) {
  if (process.env.REACT_APP_ENABLE_EMAILS !== "true") {
    console.log("No emails for this environment");
    return;
  }
  if (currentUserProfile?.isTester === true) {
    console.log("No emails for this user");
    return;
  }

  const fullName = [
    signupData.firstName,
    signupData.middleName,
    signupData.lastName,
  ]
    .filter(Boolean)
    .join(" ");

  const mailMessage = {
    subject: `Free Trial Signup Request - ${fullName}`,
    text: `New free trial signup request:

Name: ${fullName}
Email: ${signupData.email}
Real Estate License #: ${signupData.license}
Brokerage: ${signupData.brokerageName}

Existing account email: ${currentUserProfile?.email || "Unknown"}`,
    html: `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2>New Free Trial Signup Request</h2>
        <p><strong>Name:</strong> ${fullName}</p>
        <p><strong>Email:</strong> ${signupData.email}</p>
        <p><strong>Real Estate License #:</strong> ${signupData.license}</p>
        <p><strong>Brokerage:</strong> ${signupData.brokerageName}</p>
        <hr />
        <p style="color: #666; font-size: 12px;">
          Existing account email: ${currentUserProfile?.email || "Unknown"}
        </p>
      </div>
    `,
  };

  const emailData = {
    to: ["support@transactioner.com"],
    replyTo: signupData.email,
    message: mailMessage,
  };

  const mailRef = collection(db, "mail");
  try {
    if (process.env.REACT_APP_ENVIRONMENT === "development") {
      console.log("EMAIL THAT WOULD BE SENT: ", emailData);
      return;
    } else {
      return addDoc(mailRef, emailData);
    }
  } catch (error) {
    throw error;
  }
}

///////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////
///////////////////////  COMPLIANCE  //////////////////////////
///////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////

export function fetchComplianceChecklistTemplateFromDb(stateProvince, agentRepresents = "Buyer") {
  const templatesRef = collection(db, "complianceChecklistTemplates");
  console.log("stateProvince: ", stateProvince, " agentRepresents: ", agentRepresents);
  return query(
    templatesRef,
    where("stateProvince", "==", stateProvince),
    where("agentRepresents", "==", agentRepresents),
    where("isDefault", "==", true),
    limit(1)
  );
}

export function fetchComplianceChecklistTemplateByBrokerageFromDb(
  stateProvince,
  brokerageId,
  agentRepresents = "Buyer"
) {
  const templatesRef = collection(db, "complianceChecklistTemplates");
  return query(
    templatesRef,
    where("stateProvince", "==", stateProvince),
    where("brokerageId", "==", brokerageId),
    where("agentRepresents", "==", agentRepresents),
    limit(1)
  );
}

export function fetchComplianceChecklistTemplateByManagerFromDb(
  stateProvince,
  managerId,
  agentRepresents = "Buyer"
) {
  const templatesRef = collection(db, "complianceChecklistTemplates");
  return query(
    templatesRef,
    where("stateProvince", "==", stateProvince),
    where("managerId", "==", managerId),
    where("agentRepresents", "==", agentRepresents),
    limit(1)
  );
}

export async function updateComplianceItemStatusInDb(
  transactionId,
  itemId,
  status,
  requirement = null
) {
  const complianceRef = doc(
    db,
    "transactions",
    transactionId,
    "complianceItems",
    itemId
  );

  const updateData = {
    status: status,
    updatedAt: serverTimestamp(),
  };

  // If requirement is provided, update it as well
  if (requirement !== null) {
    updateData.requirement = requirement;
  }

  // Use setDoc with merge to create the document if it doesn't exist
  return setDoc(complianceRef, updateData, { merge: true });
}

export async function addComplianceItemToDb(transactionId, item) {
  const complianceRef = collection(
    db,
    "transactions",
    transactionId,
    "complianceItems"
  );
  return addDoc(complianceRef, {
    ...item,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export function fetchComplianceItemsFromDb(transactionId) {
  const complianceRef = collection(
    db,
    "transactions",
    transactionId,
    "complianceItems"
  );
  // Return the collection reference without ordering - items may not have an order field
  return complianceRef;
}

// Fetch all compliance templates (for admin)
export function fetchAllComplianceTemplatesFromDb() {
  const templatesRef = collection(db, "complianceChecklistTemplates");
  return query(templatesRef, orderBy("stateProvince", "asc"), orderBy("name", "asc"));
}

// Fetch compliance templates by state (for admin)
export function fetchComplianceTemplatesByStateFromDb(stateProvince) {
  const templatesRef = collection(db, "complianceChecklistTemplates");
  return query(
    templatesRef,
    where("stateProvince", "==", stateProvince),
    orderBy("name", "asc")
  );
}

// Fetch compliance templates for a brokerage (for managing brokers)
export function fetchComplianceTemplatesByBrokerageIdFromDb(brokerageId) {
  const templatesRef = collection(db, "complianceChecklistTemplates");
  return query(
    templatesRef,
    where("brokerageId", "==", brokerageId)
  );
}

// Fetch default (state-wide) compliance templates for a state
export function fetchDefaultComplianceTemplatesByStateFromDb(stateProvince) {
  const templatesRef = collection(db, "complianceChecklistTemplates");
  return query(
    templatesRef,
    where("stateProvince", "==", stateProvince),
    where("isDefault", "==", true)
  );
}

// Add new compliance template
export function addComplianceTemplateToDb(templateData) {
  const templateId = doc(collection(db, "complianceChecklistTemplates")).id;
  return setDoc(doc(db, "complianceChecklistTemplates", templateId), {
    ...templateData,
    id: templateId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

// Update compliance template
export function updateComplianceTemplateInDb(templateId, templateData) {
  const docRef = doc(db, "complianceChecklistTemplates", templateId);
  return updateDoc(docRef, {
    ...templateData,
    updatedAt: serverTimestamp(),
  });
}

// Delete compliance template
export function deleteComplianceTemplateInDb(templateId) {
  const docRef = doc(db, "complianceChecklistTemplates", templateId);
  return deleteDoc(docRef);
}

// Copy compliance template
export function copyComplianceTemplateToDb(template) {
  const templateId = doc(collection(db, "complianceChecklistTemplates")).id;
  const { id, createdAt, updatedAt, ...templateData } = template;
  return setDoc(doc(db, "complianceChecklistTemplates", templateId), {
    ...templateData,
    id: templateId,
    name: `${template.name} (Copy)`,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}